import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import ReactMarkdown from "react-markdown";
import { AiWatermark } from "@/components/ai-watermark";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";
import {
  Sparkles,
  ScrollText,
  Pin,
  Pencil,
  Loader2,
  Trash2,
  BookOpen,
  Lock,
  Maximize2,
  Minimize2,
  MessageSquare,
  Share2,
  FolderOpen,
  ArrowLeft,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  chartPrefillSearch,
  parseChartPrefill,
  type ChartPrefillSearch,
} from "@/lib/chart-prefill";

function YinYang({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="currentColor">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="4" />
      <path
        d="M 50 4 A 46 46 0 0 0 50 96 A 23 23 0 0 0 50 50 A 23 23 0 0 1 50 4 M 50 19.5 A 7.5 7.5 0 1 0 50 34.5 A 7.5 7.5 0 1 0 50 19.5 M 50 65.5 A 7.5 7.5 0 1 0 50 80.5 A 7.5 7.5 0 1 0 50 65.5 Z"
        fillRule="evenodd"
        fill="currentColor"
      />
    </svg>
  );
}
import { SiteHeader } from "@/components/site-header";
import { DivinerPicker } from "@/components/diviner-picker";
import { MysticChat } from "@/components/mystic-chat";
import { ShareDialog } from "@/components/share/share-dialog";
import { TuviChartsDrawer } from "@/components/tuvi/tuvi-charts-drawer";
import { CungsPaywall } from "@/components/tuvi/cungs-paywall";
import { TuviHuyenKhi, CungScoreBadge, bandKeyForScore } from "@/components/tuvi-huyen-khi";
import { BoideSettingsDialog } from "@/components/boide-settings-dialog";
import { TrustProbeDialog } from "@/components/trust-probe-dialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useRegisterTour } from "@/components/tour/tour-provider";
import {
  useBoideUser,
  displayName,
  refreshBoideUser,
  getDefaultDivinerId,
  isProfileIncomplete,
  isProfilePromptDismissed,
  markProfilePromptDismissed,
} from "@/hooks/use-boide-user";
import {
  CUNG_NAMES,
  CHI_POSITIONS,
  CHINH_TINH,
  PHU_TINH,
  TIEU_CAT,
  TIEU_HUNG,
  TIEU_TINH,
  TUAN_TRIET,
  CAN_SHORT,
  TIEU_HAN_INDEX,
  elementClass,
  hash,
  mulberry32,
  generateChart,
  normalizeElementInput,
  mauToElement,
  nguHanhToElement,
  starElement,
  normalizeStarName,
  HUNG_LOOKUP,
  RIGHT_COLUMN_STARS,
  LEFT_COLUMN_PRIORITY,
  STAR_TYPE_ORDER,
  isRightColumnStar,
  isLeftColumnStar,
  sortColumnStars,
  shortTrangThai,
  formatCanChi,
  formatHour,
  formatNamXem,
  buildViewFromApi,
  TAM_HOP,
  XUNG_CHIEU,
  cellCenter,
  CungCell,
  InfoRow,
  type Element,
  type Star,
  type ColumnStar,
  type CungData,
  type ApiMeta,
  type ChartView,
} from "@/components/tuvi/tuvi-la-so-board";
import {
  createTrustProbeCharts,
  getTuviChart,
  CHARTS_PAGE_SIZE,
  listTuviCharts,
  setTuviChartId,
  getTuviChartId,
  getChatHistory,
  getSession,
  deleteTuviChart,
  streamTuviReading,
  getTuviReading,
  getTuviLuuNien,
  unlockTuviCungs,
  getBoideToken,
  TUVI_CUNG_KEY_BY_NAME,
  type ChatMessage,
  type TuviChart,
  type TuviDaiVan,
  type TuviLuuNien,
  type TuviReading,
} from "@/lib/boide-api";
import { errorName, trackEvent } from "@/lib/analytics";
import { UnknownHourCta } from "@/components/doan-gio/unknown-hour-cta";

const formatShortDate = (dateStr: string) => {
  if (!dateStr) return "";
  const clean = dateStr.replace(/\s+/g, "");
  const parts = clean.includes("-") ? clean.split("-") : clean.split("/");
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const dd = parts[2];
      const mm = parts[1];
      const yy = parts[0].slice(-2);
      return `${dd}/${mm}/${yy}`;
    }
    // DD/MM/YYYY
    const dd = parts[0];
    const mm = parts[1];
    const yy = parts[2].slice(-2);
    return `${dd}/${mm}/${yy}`;
  }
  return dateStr;
};

export const Route = createFileRoute("/tu-vi")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { session?: string; chart?: string } & ChartPrefillSearch => ({
    // `?ho_ten=&ngay=&gio=&gioi_tinh=` — điền sẵn form rồi tự lập lá số (từ trang Đoán giờ sinh).
    ...chartPrefillSearch(search),
    session: typeof search.session === "string" ? search.session : undefined,
    // `?chart=` mở thẳng MỘT lá số đã lập — đích của trang Lịch sử (tab Luận giải).
    // Khác `?session=`: phần lớn lá số chưa từng có phiên đàm đạo nào, nên vào bằng
    // `session` là không có đường nào mở lại chúng.
    chart: typeof search.chart === "string" ? search.chart : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Lập Lá Số Tử Vi Đẩu Số Miễn Phí - Luận Giải 12 Cung Chi Tiết" },
      {
        name: "description",
        content:
          "Lập lá số Tử Vi Đẩu Số miễn phí với 12 cung, chính tinh phụ tinh và luận giải chi tiết bằng AI. Lập lá số ngay!",
      },
      { property: "og:title", content: "Lập Lá Số Tử Vi Đẩu Số Miễn Phí - Luận Giải 12 Cung" },
      { property: "og:description", content: "Lập bản mệnh 12 cung và luận giải tường tận." },
      { property: "og:url", content: "https://boide.online/tu-vi" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/tu-vi" }],
  }),
  component: TuViPage,
});

const MYSTIC_MD_COMPONENTS = {
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-[#E5B869]">{children}</strong>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="font-serif-soft italic text-[#E5B869] text-xl mt-6 mb-3">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="font-serif-soft italic text-[#E5B869] text-lg mt-5 mb-2">{children}</h3>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <h4 className="font-display tracking-[0.15em] text-[#E5B869] text-[13px] uppercase mt-4 mb-2">
      {children}
    </h4>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="flex gap-2 items-start leading-[1.7]">
      <span className="text-[#E5B869] text-[12px] mt-[5px] opacity-80 select-none">✦</span>
      <div className="flex-1">{children}</div>
    </li>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="flex flex-col gap-2 my-3 list-none !pl-0 !ml-0">{children}</ul>
  ),
};

// Năm xem hạn cho dropdown sao lưu niên: từ 10 năm trước → 10 năm sau năm hiện tại.
const YEAR_OPTIONS: number[] = (() => {
  const now = new Date().getFullYear();
  return Array.from({ length: 21 }, (_, i) => now - 10 + i);
})();

const DEMO_PROFILES: {
  name: string;
  date: string;
  time: string;
  gender: "Nam" | "Nữ";
  note: string;
}[] = [{ name: "Lan", date: "01/02/02", time: "22:00", gender: "Nữ", note: "Dữ liệu mẫu" }];

type TuviForm = {
  name: string;
  date: string;
  time: string;
  gender: "Nam" | "Nữ";
};

const EMPTY_TUVI_FORM: TuviForm = {
  name: "",
  date: "",
  time: "",
  gender: "Nam",
};

// Tam hợp: 4 nhóm chi (cùng tam hợp cục)

function parseDateString(dateStr: string): { day: number; month: number; year: number } | null {
  if (!dateStr) return null;
  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;
  const d = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  let y = parseInt(parts[2], 10);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;

  if (parts[2].length === 2) {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const yearInCentury = currentYear % 100;
    if (y > yearInCentury + 10) {
      y += currentCentury - 100;
    } else {
      y += currentCentury;
    }
  }

  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1000 || y > 9999) return null;

  return { day: d, month: m, year: y };
}

function formatDateToDmYy(day: number, month: number, year: number): string {
  const dStr = String(day).padStart(2, "0");
  const mStr = String(month).padStart(2, "0");
  const yStr = String(year % 100).padStart(2, "0");
  return `${dStr}/${mStr}/${yStr}`;
}

function toDmYy(dateStr: string): string {
  if (!dateStr) return "";
  if (dateStr.includes("/")) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      return formatDateToDmYy(d, m, y);
    }
  }
  return dateStr;
}

function TuViPage() {
  const search = Route.useSearch();
  const { session, chart: chartParam } = search;
  // Chốt MỘT lần lúc mount: URL bị dọn ngay sau khi áp, nhưng các effect tự mở lá số
  // cũ vẫn phải biết lượt này đến để lập lá số MỚI — nếu không chúng đè lá số cũ lên.
  const [prefill] = useState(() => (search.session ? null : parseChartPrefill(search)));
  const { user, ready } = useBoideUser();
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const justCreatedChartRef = useRef(false);
  const prevTrustProbeOpenRef = useRef(false);
  // Đánh dấu đã cam kết auto-load (ngay trước khi bắt đầu) để các lần effect re-run không khởi
  // động lại. Thành công thể hiện qua `apiChart`; nếu mọi ứng viên fail thì mở lại để thử tiếp.
  const autoloadDoneRef = useRef(false);

  // Danh sách lá số đã lập từ server — nguồn sự thật để fallback khi localStorage trống/stale
  // (giống Bát Tự). queryFn trả về mảng đã sắp theo mới nhất → [0] là lá số gần nhất.
  // Chỉ cần lá số GẦN NHẤT để hydrate — trang 1 (10 lá số) là quá đủ; danh sách đầy đủ
  // xem ở drawer "Lá số đã lập", nơi có phân trang riêng.
  const savedCharts = useQuery({
    queryKey: ["tuvi-charts", { page: 1, limit: CHARTS_PAGE_SIZE }],
    queryFn: () => listTuviCharts(1, CHARTS_PAGE_SIZE),
    enabled: Boolean(user),
  });

  useEffect(() => {
    if (!ready || !user) return;
    // Đã bỏ qua trong phiên duyệt web này thì không nhắc lại (kể cả khi `user`
    // đổi reference sau mỗi refreshBoideUser — chat/khai mở/nạp tiền...).
    if (isProfilePromptDismissed()) return;
    if (isProfileIncomplete(user.profile)) setProfileDialogOpen(true);
  }, [ready, user]);

  const handleProfileDialogChange = (open: boolean) => {
    setProfileDialogOpen(open);
    if (!open) markProfilePromptDismissed();
  };
  const [form, setForm] = useState<TuviForm>(() => ({ ...EMPTY_TUVI_FORM }));
  const [seed, setSeed] = useState("tuvi-preview");
  useEffect(() => {
    if (!user || session || prefill) return;
    // Chỉ prefill form từ profile khi CHƯA lập lá số. Khi đã có apiChart, seed = chart.id
    // và không được ghi đè — nếu không, mỗi lần refreshBoideUser() (sau khi chat/khai mở)
    // sẽ đổi seed → đổi resetKey → MysticChat remount → mất tin nhắn vừa gửi.
    if (apiChart) return;
    const profile = user.profile;
    const normalizedGender = String(profile?.gender || "")
      .trim()
      .toLowerCase();
    const profileName = displayName(user, "");
    const profileGender =
      normalizedGender === "female" || normalizedGender === "nữ" || normalizedGender === "nu"
        ? "Nữ"
        : normalizedGender === "male" || normalizedGender === "nam"
          ? "Nam"
          : null;

    // Prefill từng trường có dữ liệu thật, nhưng không ghi đè phần người dùng đã nhập.
    // Giờ sinh có thể chưa có trong hồ sơ nên vẫn để trống để người dùng chủ động chọn.
    setForm((current) => ({
      name: current.name || profileName,
      date: current.date || (profile?.birth_date ? toDmYy(profile.birth_date) : ""),
      time: current.time || profile?.birth_time?.slice(0, 5) || "",
      gender: profileGender ?? current.gender,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, session]);

  const [opened, setOpened] = useState(false);
  const [trustProbeId, setTrustProbeId] = useState<string | null>(null);
  const [trustProbeOpen, setTrustProbeOpen] = useState(false);
  const [hoveredCung, setHoveredCung] = useState<string | null>(null);
  const [pinnedCung, setPinnedCung] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState(0);
  const [rippleKey, setRippleKey] = useState(0);
  const [chartKey, setChartKey] = useState(1);
  const [formExpanded, setFormExpanded] = useState(true);
  const [apiChart, setApiChart] = useState<TuviChart | null>(null);
  // Năm xem hạn — điều khiển overlay sao lưu niên (lưu động) lên lá số.
  const [selectedYear, setSelectedYear] = useState<number>(() => new Date().getFullYear());
  const [luuNien, setLuuNien] = useState<TuviLuuNien | null>(null);

  // Auto-start board tour after trust probe questionnaire is completed/closed
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMaskActive, setShareMaskActive] = useState(false);
  const [chartsDrawerOpen, setChartsDrawerOpen] = useState(false);
  const [selectedDivinerId, setSelectedDivinerId] = useState("cu_an");
  const [historySessionId, setHistorySessionId] = useState<string | undefined>();
  const [historyMessages, setHistoryMessages] = useState<ChatMessage[]>([]);

  // Set default diviner ID based on user profile when it loads
  useEffect(() => {
    if (!session && user?.profile) {
      setSelectedDivinerId(getDefaultDivinerId(user.profile));
    }
  }, [user, session]);

  // Cuộn target của một bước tour vào đúng vị trí trong khung cuộn của trang.
  // Gọi ở `tour-prescroll` (TRƯỚC khi joyride đổi bước → vẽ khung đúng ngay, tránh "lệch rồi nhảy")
  // và lặp lại ở `tour-event` step:before làm lớp phòng hờ cho các dịch chuyển layout còn sót.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToTuviStep = (tourId: string, stepIndex: number) => {
      if (tourId === "tuvi") {
        if (stepIndex === 1 || stepIndex === 2) {
          setFormExpanded(true);
        }
      } else if (tourId === "tuvi-board") {
        if (stepIndex === 1) {
          if (apiChart) setFormExpanded(false);
        }
      } else if (tourId === "tuvi-reading") {
        setFormExpanded(false);
        setOpened(true);
        // Cả hai bước reading đều ở tab Luận giải trên mobile: bước "đọc luận
        // giải" trỏ phần Đại cục, bước "xem từng cung" trỏ thanh nav cung
        // (Đại cục / Mệnh / Phụ Mẫu...) để bấm hoặc lướt ngang — KHÔNG quay về
        // tab Lá số. (Desktop hiện cả hai cột nên setMobilePane vô hại.)
        if (stepIndex === 1 || stepIndex === 2) setMobilePane("reading");
      }

      let stepSelectors: string[] = [];
      if (tourId === "tuvi") {
        stepSelectors = [
          "body", // Index 0: Welcome
          '[data-tour="tuvi-form"]', // Index 1
          '[data-tour="tuvi-submit"]', // Index 2
        ];
      } else if (tourId === "tuvi-board") {
        stepSelectors = [
          "body",
          '[data-tour="tuvi-khaimo-mobile"]', // Index 1
        ];
      } else if (tourId === "tuvi-reading") {
        stepSelectors = [
          "body",
          '[data-tour="tuvi-reading-anchor"]', // Index 1
          '[data-tour="tuvi-cung-nav"]', // Index 2 (mobile: thanh nav cung; desktop fallback ô cung)
        ];
      }

      const selector = stepSelectors[stepIndex];
      if (!selector || selector === "body") return;

      const container = document.querySelector('[data-tour="tuvi-scroll-container"]');

      const isVisible = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };

      const pickVisibleElement = (sel: string): HTMLElement | null => {
        const elements = Array.from(document.querySelectorAll(sel));
        for (const el of elements) {
          if (el instanceof HTMLElement && isVisible(el)) {
            return el;
          }
        }
        const first = document.querySelector(sel);
        return first instanceof HTMLElement ? first : null;
      };

      let target = pickVisibleElement(selector);
      if (tourId === "tuvi-board" && stepIndex === 1) {
        if (!target || !isVisible(target)) {
          target = pickVisibleElement('[data-tour="tuvi-khaimo"]');
        }
      } else if (tourId === "tuvi-reading" && stepIndex === 1) {
        if (!target || !isVisible(target)) {
          target = pickVisibleElement('[data-tour="tuvi-reading"]');
        }
      } else if (tourId === "tuvi-reading" && stepIndex === 2) {
        // Thanh nav cung chỉ hiện trên mobile (lg:hidden); desktop dùng ô cung trên lá số.
        if (!target || !isVisible(target)) {
          target = pickVisibleElement('[data-cung-cell="true"]');
        }
      }

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: Math.max(0, relativeTop - 24),
          behavior: "auto",
        });

        // Dispatch resize event immediately so Joyride recalculates coordinates instantly
        window.dispatchEvent(new Event("resize"));

        // Keep a brief interval to handle any potential layout shifts or rendering delays
        const interval = setInterval(() => {
          window.dispatchEvent(new Event("resize"));
        }, 50);
        setTimeout(() => {
          clearInterval(interval);
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    };

    const handleTourEvent = (e: Event) => {
      const { tourId, stepIndex, type } = (e as CustomEvent).detail || {};
      if (tourId !== "tuvi" && tourId !== "tuvi-board" && tourId !== "tuvi-reading") return;
      if (type === "step:after" || type === "tour:start" || type === "step:before") {
        scrollToTuviStep(tourId, stepIndex);
      }
    };

    const handlePrescroll = (e: Event) => {
      const { tourId, stepIndex } = (e as CustomEvent).detail || {};
      if (tourId !== "tuvi" && tourId !== "tuvi-board" && tourId !== "tuvi-reading") return;
      scrollToTuviStep(tourId, stepIndex);
    };

    window.addEventListener("tour-event", handleTourEvent);
    window.addEventListener("tour-prescroll", handlePrescroll);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
      window.removeEventListener("tour-prescroll", handlePrescroll);
    };
  }, [session, user, apiChart]);
  // Reading = bản luận giải toàn bộ lá số (Đại cục + 12 cung) do API /reading sinh ra.
  // Khác với chat: không gắn session, lưu thẳng vào chart, có thể "khai mở lại".
  // Lưu nguyên object để FE render Đại cục riêng, từng cung riêng khi click.
  const [reading, setReading] = useState<TuviReading | null>(null);
  const [readingError, setReadingError] = useState<string>("");
  const [readingLoading, setReadingLoading] = useState(false);
  const [cungsUnlocking, setCungsUnlocking] = useState(false);
  const unlockingRef = useRef(false);
  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);
  const [isCungCollapsed, setIsCungCollapsed] = useState(false);
  const [showOverviewChat, setShowOverviewChat] = useState(false);
  const [showCungChat, setShowCungChat] = useState(false);

  // Reset showCungChat when pinnedCung changes (so user starts by reading details)
  useEffect(() => {
    setShowCungChat(false);
  }, [pinnedCung]);

  const readingAbortRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const readingRef = useRef<HTMLElement>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [mobilePane, setMobilePane] = useState<"chart" | "reading">("chart");
  const mobileWorkspaceRef = useRef<HTMLDivElement>(null);
  const mobileReadingNavRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (showOverviewChat || showCungChat) return;
    if (e.touches.length > 1) return;
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (showOverviewChat || showCungChat) return;
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    // Phải lướt ngang nhiều hơn lướt dọc để tránh nhầm với cuộn dọc
    if (Math.abs(deltaX) > 60 && Math.abs(deltaY) < 40) {
      const tabs: (string | null)[] = [null, ...CUNG_NAMES];
      const currentIndex = tabs.indexOf(pinnedCung);

      if (deltaX < 0) {
        // Lướt sang trái: chuyển sang cung tiếp theo (tiến lên)
        const nextIndex = currentIndex + 1;
        if (nextIndex < tabs.length) {
          selectMobileReading(tabs[nextIndex]);
        }
      } else {
        // Lướt sang phải: chuyển sang cung trước đó (lùi lại)
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
          selectMobileReading(tabs[prevIndex]);
        }
      }
    }
  };

  // Scale-to-fit cho lưới lá số trên mobile: giữ nguyên layout 640px dày đặc
  // rồi co toàn bộ vừa chiều ngang màn hình (thay vì tràn/cắt như cũ).
  const chartViewportRef = useRef<HTMLDivElement>(null);
  const chartInnerRef = useRef<HTMLElement>(null);
  const [chartScale, setChartScale] = useState(1);
  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);
  // Khi zoom = true: hiển thị lưới 100% và cho cuộn (đọc chữ to); mặc định fit màn hình.
  const [chartZoomed, setChartZoomed] = useState(false);

  // Mỗi lá số mới luôn bắt đầu ở chế độ vừa màn hình.
  useEffect(() => setChartZoomed(false), [apiChart?.id]);

  // Register tour for Tử Vi based on current phase
  useRegisterTour(
    formExpanded ? "tuvi" : apiChart ? (reading ? "tuvi-reading" : "tuvi-board") : "tuvi",
    !loading && !trustProbeOpen && !readingLoading && !cungsUnlocking,
  );

  useEffect(() => {
    if (viewMode !== "grid" || typeof window === "undefined") return;
    const viewport = chartViewportRef.current;
    const inner = chartInnerRef.current;
    if (!viewport || !inner) return;

    const update = () => {
      const isMobile = window.matchMedia("(max-width: 1023px)").matches;
      const naturalWidth = inner.offsetWidth;
      // Desktop hoặc khi user phóng to: dùng size thật, không scale.
      if (!isMobile || naturalWidth === 0 || chartZoomed) {
        setChartScale(1);
        setChartHeight(undefined);
        return;
      }
      const available = viewport.clientWidth;
      // Tab Luận giải ẩn lá số trên mobile; chờ tab Lá số hiện lại rồi mới đo.
      if (available === 0) return;
      const scale = available < naturalWidth ? available / naturalWidth : 1;
      setChartScale(scale);
      setChartHeight(scale < 1 ? inner.offsetHeight * scale : undefined);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    ro.observe(inner);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  // Lá số lưu sẵn được tải bất đồng bộ sau lần render đầu tiên. Cần chạy lại khi
  // apiChart xuất hiện, nếu không lưới 640px sẽ bị giữ ở tỷ lệ 100% trên mobile.
  }, [viewMode, chartKey, opened, loading, chartZoomed, apiChart?.id, mobilePane]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;

    (async () => {
      try {
        const [detail, history] = await Promise.all([getSession(session), getChatHistory(session)]);
        if (cancelled) return;

        const chartId = detail.metadata?.tuvi_chart_id;
        if (chartId) {
          const [chartDetail, cachedReading] = await Promise.all([
            getTuviChart(chartId),
            getTuviReading(chartId),
          ]);
          if (cancelled) return;
          setApiChart(chartDetail);
          setTuviChartId(chartDetail.id);
          hydrateFormFromChart(chartDetail);
          setSeed(chartDetail.id);
          setReading(cachedReading);
          setReadingError("");
        } else {
          setReading(null);
          setReadingError("");
        }

        setSelectedDivinerId(detail.diviner_id || "cu_an");
        setHistorySessionId(detail.id);
        setHistoryMessages(history.messages);
        setOpened(true);
        setFormExpanded(false);

        const isCungSession = CUNG_NAMES.includes(detail.subject);
        if (isCungSession) {
          setPinnedCung(detail.subject);
          setShowCungChat(true);
          setShowOverviewChat(false);
        } else {
          setShowOverviewChat(true);
          setShowCungChat(false);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Không tải được phiên Tử Vi");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session]);

  // On mount: hydrate lá số gần nhất. Ưu tiên ID lưu trong localStorage; nếu trống/stale thì
  // fallback về lá số mới nhất từ server (savedCharts[0]) — nhờ vậy luôn hiển thị lá số gần
  // nhất kể cả trên thiết bị/trình duyệt khác, nhất quán với trang Bát Tự.
  useEffect(() => {
    if (autoloadDoneRef.current) return; // đã cam kết / đang load → không khởi động lại
    if (!user || session) return;
    // Đến từ trang khác kèm thông tin sinh → lượt này lập lá số MỚI, không mở lá số cũ.
    if (prefill) return;
    // Skip if there's a pending restore from "Khai mở" redirect — avoid race condition
    if (sessionStorage.getItem("tuvi_pending_open")) return;
    if (apiChart) return; // đã có lá số hiển thị → coi như xong

    const storedId = getTuviChartId();
    const fallbackId = savedCharts.data?.items?.[0]?.id;
    // URL đã chỉ đích danh một lá số (`/tu-vi?chart=…`, đích của trang Lịch sử) thì nó
    // là ứng viên DUY NHẤT: rơi về lá số lưu sẵn khi id ấy hỏng là mở ra một lá số
    // khác hẳn lá số người xem vừa bấm, mà không báo gì.
    const candidates = chartParam
      ? [chartParam]
      : [storedId, fallbackId].filter(
          (id, i, arr): id is string => Boolean(id) && arr.indexOf(id) === i,
        );
    // Chưa có ứng viên nào → đợi (effect tự chạy lại khi savedCharts.data về). KHÔNG khoá.
    if (candidates.length === 0) return;

    // Cam kết load NGAY để các lần re-run (do query đổi trạng thái) bail thay vì chồng lên.
    // KHÔNG dùng cờ `cancelled`/cleanup để hủy: cleanup chạy mỗi lần re-run (không chỉ unmount)
    // và sẽ chặn setApiChart → deadlock. Cứ để load chạy tới cùng; setState sau unmount là no-op.
    autoloadDoneRef.current = true;

    void (async () => {
      for (const id of candidates) {
        let c: TuviChart;
        try {
          c = await getTuviChart(id);
        } catch {
          // ID lưu đã stale (lá số ẩn danh, đã xoá, hoặc của user khác) → dọn rồi thử fallback.
          if (id === storedId) setTuviChartId("");
          continue;
        }
        setApiChart(c);
        setTuviChartId(c.id);
        hydrateFormFromChart(c);
        // Đã có lá số → thu gọn form (hiện thanh tóm tắt + bàn lá số), không hiện màn "Lập lá số".
        setFormExpanded(false);
        // Reading tải riêng & không chặn: lỗi đọc reading không được làm mất lá số.
        try {
          const cachedReading = await getTuviReading(c.id);
          if (cachedReading) {
            setReading(cachedReading);
            setOpened(true);
          }
        } catch {
          // Không đọc được reading (network/timeout) — vẫn giữ lá số, user có thể bấm "Khai mở".
        }
        return; // đã hydrate xong
      }
      // Mọi ứng viên fail → mở lại để khi savedCharts.data về, effect thử lại fallback.
      autoloadDoneRef.current = false;
      // Id đến từ URL thì thất bại là chuyện phải nói: lá số đã bị xoá, hoặc link
      // thuộc tài khoản khác. Im lặng là người xem đứng nhìn màn "Lập lá số" và tưởng
      // lá số của mình bay mất.
      if (chartParam) {
        toast.error("Không mở được lá số này — có thể lá số đã bị xoá.");
      }
    })();
  }, [user, session, chartParam, apiChart, savedCharts.data, prefill]);

  // Điền sẵn từ URL rồi tự lập lá số. Dọn URL ngay để tải lại trang không lập thêm lá số.
  const prefillStartedRef = useRef(false);
  useEffect(() => {
    if (!prefill || prefillStartedRef.current) return;
    prefillStartedRef.current = true;
    const next: TuviForm = {
      name: prefill.name,
      date: `${String(prefill.day).padStart(2, "0")}/${String(prefill.month).padStart(2, "0")}/${prefill.year}`,
      time: prefill.time,
      gender: prefill.gender === "nu" ? "Nữ" : "Nam",
    };
    setForm(next);
    setFormExpanded(true);
    void navigate({ to: "/tu-vi", search: {}, replace: true });
    if (next.name) void lapLaSo(next);
    else toast.info("Nhập họ tên rồi bấm Lập lá số");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Sau khi đăng nhập, khôi phục form và tự động lập lá số
  useEffect(() => {
    if (!user) return;
    const raw = sessionStorage.getItem("tuvi_pending_open");
    if (!raw) return;
    sessionStorage.removeItem("tuvi_pending_open");
    // Xóa chart cũ trong localStorage để effect chart-loading không load chart cũ đè lên
    setTuviChartId("");
    let savedForm: typeof form;
    try {
      savedForm = JSON.parse(raw) as typeof form;
    } catch {
      return;
    }
    setForm(savedForm);
    void lapLaSo(savedForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function hydrateFormFromChart(c: TuviChart) {
    const date = formatDateToDmYy(c.ngay_sinh, c.thang_sinh, c.nam_sinh);
    const time = `${String(c.gio_sinh).padStart(2, "0")}:00`;
    const gender = c.gioi_tinh === "nam" ? "Nam" : "Nữ";
    setForm({ name: c.ho_ten, date, time, gender });
    setSelectedYear(c.nam_xem ?? new Date().getFullYear());
    setSeed(`${c.id}`);
  }

  // Mở lại 1 lá số cũ (từ drawer "Lá số đã lập").
  async function loadChartById(id: string) {
    try {
      const [c, cachedReading] = await Promise.all([getTuviChart(id), getTuviReading(id)]);
      setApiChart(c);
      setTuviChartId(c.id);
      hydrateFormFromChart(c);
      setHistorySessionId(undefined);
      setHistoryMessages([]);
      if (cachedReading) {
        setReading(cachedReading);
        setOpened(true);
        setFormExpanded(false);
      } else {
        setReading(null);
        setOpened(false);
        setFormExpanded(false);
      }
    } catch (e) {
      toast.error(`Không tải được lá số: ${(e as Error).message}`);
    }
  }

  // Tải sao lưu niên cho năm xem mỗi khi đổi lá số hoặc đổi năm.
  useEffect(() => {
    if (!apiChart?.id) {
      setLuuNien(null);
      return;
    }
    let alive = true;
    getTuviLuuNien(apiChart.id, selectedYear)
      .then((ln) => {
        if (alive) setLuuNien(ln);
      })
      .catch(() => {
        if (alive) setLuuNien(null);
      });
    return () => {
      alive = false;
    };
  }, [apiChart?.id, selectedYear]);

  const chart = useMemo(() => {
    if (!apiChart) return generateChart(seed);
    // Khi đã có sao lưu niên (đúng năm xem): dùng cung_list đã overlay (sao lưu kèm màu),
    // và bỏ luu_nien để buildViewFromApi không overlay chồng.
    const source =
      luuNien && luuNien.cung_list?.length
        ? { ...apiChart, cung_list: luuNien.cung_list, luu_nien: undefined }
        : apiChart;
    return buildViewFromApi(source);
  }, [apiChart, seed, luuNien]);

  const parsedDate = useMemo(() => {
    return parseDateString(form.date);
  }, [form.date]);
  const isFormComplete = Boolean(form.name.trim() && parsedDate && form.time);

  const sortedCungs = useMemo(() => {
    return [...chart.cungs].sort((a, b) => {
      const aIdx = CUNG_NAMES.indexOf(a.cung);
      const bIdx = CUNG_NAMES.indexOf(b.cung);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });
  }, [chart.cungs]);

  async function lapLaSo(input: TuviForm): Promise<TuviChart | null> {
    const cleanName = input.name.trim();
    if (!cleanName) {
      toast.error("Vui lòng nhập họ tên trước khi lập lá số");
      return null;
    }
    if (!input.time) {
      toast.error("Vui lòng chọn giờ sinh trước khi lập lá số");
      return null;
    }

    setLoading(true);
    try {
      const parsed = parseDateString(input.date);
      if (!parsed) {
        toast.error("Ngày sinh không đúng định dạng dd/mm/yy (ví dụ: 25/05/85)");
        return null;
      }
      const { day: d, month: m, year: y } = parsed;
      const [hh, mm] = input.time.split(":").map(Number);
      trackEvent("tuvi_chart_create_start", { gender: input.gender });
      const createdPair = await createTrustProbeCharts({
        source: "tuvi",
        use_profile: false,
        ho_ten: cleanName,
        nam: y,
        thang: m,
        ngay: d,
        gio: hh,
        phut: Number.isFinite(mm) ? mm : 0,
        gioi_tinh: input.gender === "Nam" ? "nam" : "nu",
        is_lunar: false,
        nam_xem: selectedYear,
      });
      const created = createdPair.tuvi_chart;
      setApiChart(created);
      setTuviChartId(created.id);
      setTrustProbeId(createdPair.probe.id);
      // setTrustProbeOpen(true); // Tạm thời tắt tính năng hỏi lại khi lập lá số
      setSeed(created.id);
      setOpened(false);
      setPinnedCung(null);
      setHoveredCung(null);
      setFormExpanded(false);
      setChartKey((k) => k + 1);
      setReading(null);
      setReadingError("");
      justCreatedChartRef.current = true;
      trackEvent("tuvi_chart_create_success", { gender: input.gender });
      trackEvent("trust_probe_created", {
        source: "tuvi",
        status: createdPair.probe.status,
      });
      toast.success(`Đã lập lá số cho ${created.ho_ten}`);

      return created;
    } catch (err) {
      trackEvent("tuvi_chart_create_error", { error_name: errorName(err), gender: input.gender });
      toast.error(err instanceof Error ? err.message : "Không lập được lá số");
      return null;
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormComplete) {
      toast.error("Vui lòng điền đủ họ tên, ngày sinh và giờ sinh");
      return;
    }
    void lapLaSo(form);
  };

  const loadDemo = (p: (typeof DEMO_PROFILES)[number]) => {
    const next = { name: p.name, date: p.date, time: p.time, gender: p.gender };
    setForm(next);
    setFormExpanded(true);
  };

  const revealReading = async (forceRefresh = false, chartIdOverride?: string) => {
    const chartId = chartIdOverride ?? apiChart?.id;
    if (!chartId) return;

    readingAbortRef.current?.abort();
    const ctrl = new AbortController();
    readingAbortRef.current = ctrl;

    setReadingLoading(true);
    setReadingError("");
    if (forceRefresh) setReading(null);
    setIsOverviewCollapsed(false);
    trackEvent("tuvi_reading_start", { force_refresh: forceRefresh });
    try {
      for await (const event of streamTuviReading(chartId, {
        forceRefresh,
        year: selectedYear,
        signal: ctrl.signal,
      })) {
        if (event.type === "dai_cuc") {
          setReading((prev) => ({
            chart_id: chartId,
            cungs: prev?.cungs ?? [],
            generated_at: prev?.generated_at ?? "",
            model: prev?.model ?? null,
            cached: prev?.cached ?? false,
            dai_cuc: event.dai_cuc,
          }));
        } else if (event.type === "cungs") {
          setReading((prev) => ({
            chart_id: chartId,
            dai_cuc: prev?.dai_cuc ?? "",
            generated_at: prev?.generated_at ?? "",
            model: prev?.model ?? null,
            cached: prev?.cached ?? false,
            cungs: event.cungs,
          }));
        } else if (event.type === "done") {
          trackEvent("tuvi_reading_success", {
            cached: event.cached,
            force_refresh: forceRefresh,
          });
          setReading((prev) =>
            prev
              ? {
                  ...prev,
                  cached: event.cached,
                  model: event.model,
                  generated_at: event.generated_at,
                  cungs_unlocked: event.cungs_unlocked,
                  cungs_locked: event.cungs_locked,
                  cungs_price: event.cungs_price,
                  cungs_free: event.cungs_free,
                }
              : null,
          );
        } else if (event.type === "error") {
          trackEvent("tuvi_reading_error", { force_refresh: forceRefresh });
          setReadingError(event.error);
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      trackEvent("tuvi_reading_error", {
        error_name: errorName(err),
        force_refresh: forceRefresh,
      });
      setReadingError(err instanceof Error ? err.message : String(err));
    } finally {
      void refreshBoideUser();
      setReadingLoading(false);
    }
  };

  const unlockCungs = async (forceRefresh = false) => {
    const chartId = apiChart?.id;
    if (!chartId) return;
    if (!getBoideToken()) {
      void navigate({ to: "/login", search: { returnTo: "/tu-vi" } });
      return;
    }
    // Sinh mới hoàn toàn 12 cung là việc tính phí → xác nhận trước.
    if (
      forceRefresh &&
      !window.confirm("Khai mở mới hoàn toàn 12 cung sẽ tính phí 10.000đ. Tiếp tục?")
    ) {
      return;
    }
    // State `cungsUnlocking` chưa kịp cập nhật giữa hai cú bấm liền nhau (và có tới hai
    // `CungsPaywall` cùng hiện) — ref chặn ngay trong cùng một nhịp.
    if (unlockingRef.current) return;
    unlockingRef.current = true;
    setCungsUnlocking(true);
    trackEvent("tuvi_unlock_cungs_start", { force_refresh: forceRefresh });
    try {
      const res = await unlockTuviCungs(chartId, { forceRefresh, year: selectedYear });
      setReading((prev) =>
        prev ? { ...prev, cungs: res.cungs, cungs_unlocked: true, cungs_locked: false } : prev,
      );
      trackEvent("tuvi_unlock_cungs_success", { price_charged: res.price_charged });
      toast.success(
        res.price_charged > 0
          ? `Đã mở 12 cung (-${res.price_charged.toLocaleString("vi-VN")}đ)`
          : "Đã mở 12 cung (miễn phí)",
      );
    } catch (err) {
      trackEvent("tuvi_unlock_cungs_error", { error_name: errorName(err) });
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      void refreshBoideUser();
      unlockingRef.current = false;
      setCungsUnlocking(false);
    }
  };

  const handleOpen = () => {
    if (!getBoideToken()) {
      sessionStorage.setItem("tuvi_pending_open", JSON.stringify(form));
      void navigate({ to: "/login", search: { returnTo: "/tu-vi" } });
      return;
    }
    trackEvent("tuvi_open_reading", { has_cached_reading: Boolean(reading) });

    // Mở khung luận giải TRƯỚC để panel render ở trạng thái opened (header "ĐẠI CỤC" — target của
    // bước tour — xuất hiện trong DOM).
    setOpened(true);
    setFlashKey((k) => k + 1);
    setRippleKey((k) => k + 1);
    setPinnedCung(null);
    setMobilePane("reading");
    requestAnimationFrame(() => {
      mobileWorkspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // Nếu reading đã có sẵn (cache) thì không gọi lại — user phải nhấn "Khai mở lại".
    if (!reading) {
      void revealReading();
    }
  };

  const handleReopen = () => {
    if (!getBoideToken()) {
      void navigate({ to: "/login", search: { returnTo: "/tu-vi" } });
      return;
    }
    trackEvent("tuvi_reopen_reading");
    setMobilePane("reading");
    requestAnimationFrame(() => {
      mobileWorkspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    void revealReading(true);
  };

  const deleteCurrentChart = async () => {
    if (!apiChart) return;
    // Nút xoá nằm cạnh "Đàm đạo" và "Chia sẻ" trong cùng một dải nút nhỏ trên
    // mobile. Bấm nhầm ở đây là mất cả lá số lẫn phần khai mở đã trả tiền.
    const ok = await confirm({
      title: "Xoá lá số đang xem?",
      description: (
        <>
          Lá số “{apiChart.ho_ten || "Ẩn danh"}” và phần khai mở đã trả tiền sẽ bị xoá. Hành động
          này không thể hoàn tác và không hoàn tiền.
        </>
      ),
      confirmLabel: "Xoá lá số",
    });
    if (!ok) return;
    setLoading(true);
    trackEvent("tuvi_chart_delete_start");
    try {
      await deleteTuviChart(apiChart.id);
      setApiChart(null);
      setTuviChartId("");
      setHistorySessionId(undefined);
      setHistoryMessages([]);
      setReading(null);
      setReadingError("");
      setOpened(false);
      setFormExpanded(true);
      setPinnedCung(null);
      setHoveredCung(null);
      if (user?.profile) {
        setSelectedDivinerId(getDefaultDivinerId(user.profile));
      } else {
        setSelectedDivinerId("cu_an");
      }
      trackEvent("tuvi_chart_delete_success");
      toast.success("Đã xóa lá số hiện tại");
    } catch (err) {
      trackEvent("tuvi_chart_delete_error", { error_name: errorName(err) });
      toast.error(err instanceof Error ? err.message : "Không xóa được lá số");
    } finally {
      setLoading(false);
    }
  };

  // Abort streaming reading on unmount
  useEffect(() => () => readingAbortRef.current?.abort(), []);

  // Click outside chart/aside → unpin
  useEffect(() => {
    if (!pinnedCung) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // 1. If click is inside the reading details card (aside), do not unpin
      if (readingRef.current && readingRef.current.contains(target)) {
        return;
      }

      // 2. If click is inside any CungCell, do not unpin (let the CungCell's onClick handle it)
      if (target.closest('[data-cung-cell="true"]')) {
        return;
      }

      // 3. If click is inside a Radix UI portal / dialog / menu / listbox / toast, do not unpin
      if (
        target.closest("[data-radix-portal]") ||
        target.closest('[role="menu"]') ||
        target.closest('[role="dialog"]') ||
        target.closest('[role="listbox"]') ||
        target.closest(".sonner-toast") ||
        target.closest("[data-radix-focus-guard]")
      ) {
        return;
      }

      // 4. If click is inside the đàm đạo chat drawer, do not unpin (giữ chat mở khi gõ/bấm).
      if (target.closest('[data-tuvi-chat-drawer="true"]')) {
        return;
      }

      setPinnedCung(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pinnedCung]);

  // Giữ mục đang chọn ở giữa thanh điều hướng 12 cung trên mobile.
  useEffect(() => {
    if (mobilePane !== "reading" || typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 1023px)").matches) return;
    const navKey = pinnedCung ?? "__overview";
    requestAnimationFrame(() => {
      mobileReadingNavRefs.current[navKey]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  }, [mobilePane, pinnedCung]);

  // Mobile: đổi thẳng sang luận giải khi chọn cung, thay vì bắt người dùng kéo hết lá số.
  useEffect(() => {
    if (!pinnedCung) return;
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 1023px)").matches;
    if (!isMobile) return;
    setMobilePane("reading");
    requestAnimationFrame(() => {
      mobileWorkspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [pinnedCung]);

  const activeCung = pinnedCung ?? hoveredCung;
  const activeData = activeCung ? chart.cungs.find((c) => c.cung === activeCung) : null;
  // Reading giờ tách khỏi chat, nên toàn bộ historyMessages đều là follow-up Q&A.
  const followUpHistoryMessages = historyMessages;

  const switchMobilePane = (pane: "chart" | "reading") => {
    setMobilePane(pane);
    if (pane === "chart") {
      setShowOverviewChat(false);
      setShowCungChat(false);
    }
    requestAnimationFrame(() => {
      mobileWorkspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const selectMobileReading = (cung: string | null) => {
    setPinnedCung(cung);
    setHoveredCung(null);
    setShowOverviewChat(false);
    setShowCungChat(false);
    setIsOverviewCollapsed(false);
    setIsCungCollapsed(false);
    setMobilePane("reading");
  };

  return (
    <>
      <main
        data-tour="tuvi-scroll-container"
        className="app-feature-bg app-feature-bg--tu-vi desktop-chart-background desktop-chart-background--tu-vi flex-1 min-h-0 overflow-y-auto px-4 py-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:px-6 md:pb-6 lg:px-10"
      >
        <h1 className="sr-only">Lập lá số Tử Vi Đẩu Số — Bói Đê</h1>
        <div className="mx-auto max-w-[1500px] space-y-4 md:space-y-6">
          {/* Form and demo profiles wrapper */}
          <div className="space-y-4 md:space-y-6">
            {/* Demo profiles */}
            <div className="hidden md:flex flex-wrap items-center justify-center md:justify-start gap-1.5 rounded-2xl border border-gold-soft bg-card/40 px-3 py-2 backdrop-blur md:px-4 md:py-3">
              <span className="hidden md:inline font-display text-[10px] tracking-[0.3em] text-primary">
                THỬ NHANH:
              </span>
              {DEMO_PROFILES.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => loadDemo(p)}
                  className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-gold-soft bg-background/40 px-3 py-1 text-[11px] text-foreground/90 transition hover:border-primary/60 hover:text-primary"
                  title={`Điền ${p.note.toLowerCase()} • ${p.name}, ${p.date} ${p.time}`}
                >
                  <Sparkles className="h-3 w-3 text-primary" />
                  Dùng dữ liệu mẫu
                  <span className="text-muted-foreground">({p.name})</span>
                </button>
              ))}

              <div className="w-full md:w-auto md:ml-auto flex items-center justify-center md:justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setChartsDrawerOpen(true)}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-gold-soft bg-background/20 px-4 py-1.5 font-display text-[10px] tracking-widest text-foreground/90 backdrop-blur transition hover:border-primary/60 hover:text-primary"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  LÁ SỐ ĐÃ LẬP
                </button>
                <Link
                  to="/tu-vi-chi-tiet"
                  className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#E5B869]/50 bg-background/20 px-4 py-1.5 font-display text-[10px] tracking-widest text-[#E5B869] backdrop-blur transition hover:border-[#E5B869] hover:bg-[#E5B869]/10"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  TÌM HIỂU TỬ VI
                </Link>
              </div>
            </div>

            {/* Form */}
            {formExpanded ? (
              <>
                {/* Mobile-only: thanh "Lá số đã lập" ở trên (hidden md:flex) nên không hiện
                    trên mobile — thêm nút riêng để mở drawer ngay tại màn lập lá số. */}
                <button
                  type="button"
                  onClick={() => setChartsDrawerOpen(true)}
                  className="md:hidden w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold-soft bg-background/20 px-4 py-2 font-display text-[10px] tracking-widest text-foreground/90 backdrop-blur transition hover:border-primary/60 hover:text-primary"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  LÁ SỐ ĐÃ LẬP
                </button>
                <form
                  onSubmit={onSubmit}
                  data-tour="tuvi-form"
                  className="flex flex-wrap items-end gap-3 md:gap-4 rounded-2xl border border-gold-soft bg-card/60 p-4 md:p-5 backdrop-blur"
                >
                  <div className="flex basis-full items-center justify-between gap-3 md:hidden">
                    <p className="text-xs text-muted-foreground">
                      Dùng thông tin thật của bạn hoặc thử trước với một hồ sơ mẫu.
                    </p>
                    <button
                      type="button"
                      onClick={() => loadDemo(DEMO_PROFILES[0])}
                      className="shrink-0 rounded-full border border-gold-soft bg-background/50 px-3 py-2 text-xs font-medium text-primary"
                    >
                      Dùng dữ liệu mẫu
                    </button>
                  </div>
                  {/* basis-full: chiếm trọn hàng đầu trên mobile — flex-1 (basis 0) sẽ đè w-full trong flex-wrap */}
                  <div className="basis-full sm:basis-auto sm:flex-1">
                    <Field label="Họ tên">
                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input-mystic"
                        placeholder="Nhập họ tên"
                        autoComplete="name"
                        required
                      />
                    </Field>
                  </div>
                  <Field label="Ngày sinh (Dương lịch)">
                    <div className="flex gap-2 w-full sm:w-auto">
                      <select
                        value={parsedDate?.day ?? ""}
                        onChange={(e) => {
                          const newDay = Number(e.target.value);
                          const m = parsedDate?.month ?? 1;
                          const y = parsedDate?.year ?? 2000;
                          setForm({ ...form, date: formatDateToDmYy(newDay, m, y) });
                        }}
                        className="input-mystic flex-1 min-w-[75px]"
                        required
                      >
                        <option value="" disabled>
                          Ngày
                        </option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>
                            {String(d).padStart(2, "0")}
                          </option>
                        ))}
                      </select>

                      <select
                        value={parsedDate?.month ?? ""}
                        onChange={(e) => {
                          const newMonth = Number(e.target.value);
                          const d = parsedDate?.day ?? 1;
                          const y = parsedDate?.year ?? 2000;
                          setForm({ ...form, date: formatDateToDmYy(d, newMonth, y) });
                        }}
                        className="input-mystic flex-1 min-w-[75px]"
                        required
                      >
                        <option value="" disabled>
                          Tháng
                        </option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>
                            {String(m).padStart(2, "0")}
                          </option>
                        ))}
                      </select>

                      <select
                        value={parsedDate?.year ?? ""}
                        onChange={(e) => {
                          const newYear = Number(e.target.value);
                          const d = parsedDate?.day ?? 1;
                          const m = parsedDate?.month ?? 1;
                          setForm({ ...form, date: formatDateToDmYy(d, m, newYear) });
                        }}
                        className="input-mystic flex-1 min-w-[95px]"
                        required
                      >
                        <option value="" disabled>
                          Năm
                        </option>
                        {Array.from(
                          { length: new Date().getFullYear() - 1930 + 1 },
                          (_, i) => new Date().getFullYear() - i,
                        ).map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Field>
                  <Field label="Giờ sinh" required>
                    <input
                      type="time"
                      value={form.time}
                      onChange={(e) => setForm({ ...form, time: e.target.value })}
                      className="input-mystic"
                      required
                      aria-invalid={!form.time}
                    />
                  </Field>
                  <Field label="Giới tính">
                    <select
                      value={form.gender}
                      onChange={(e) => setForm({ ...form, gender: e.target.value as "Nam" | "Nữ" })}
                      className="input-mystic min-w-[68px]"
                    >
                      <option>Nam</option>
                      <option>Nữ</option>
                    </select>
                  </Field>
                  <Field label="Năm xem">
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                      className="input-mystic min-w-[92px]"
                    >
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {/* Cả hàng riêng, nằm ngoài <label> của ô giờ (xem UnknownHourCta). */}
                  <UnknownHourCta className="basis-full" />
                  <button
                    type="submit"
                    data-tour="tuvi-submit"
                    disabled={loading || !isFormComplete}
                    className="w-full sm:w-auto mt-2 sm:mt-0 sm:ml-auto inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-7 py-2.5 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {loading ? "Đang lập..." : "Lập lá số"}
                  </button>
                  <style>{`
                  .input-mystic {
                    width: 100%;
                    background: oklch(0.995 0.006 78 / 0.92);
                    border: 1px solid oklch(0.68 0.12 80 / 0.35);
                    color: var(--color-foreground);
                    padding: 0.55rem 0.85rem;
                    border-radius: 0.6rem;
                    /* 16px trên mobile — dưới 16px Safari iOS sẽ tự zoom khi focus input */
                    font-size: 1rem;
                    outline: none;
                    color-scheme: light;
                    box-shadow: inset 0 1px 2px oklch(0.24 0.015 75 / 0.06);
                  }
                  @media (min-width: 640px) {
                    .input-mystic {
                      font-size: 0.875rem;
                    }
                  }
                  .input-mystic:focus {
                    border-color: oklch(0.68 0.12 80 / 0.75);
                    background: oklch(1 0.004 78 / 0.98);
                  }
                  .dark .input-mystic {
                    background: oklch(0.18 0.008 85 / 0.6);
                    border-color: oklch(0.82 0.14 85 / 0.25);
                    color-scheme: dark;
                    box-shadow: none;
                  }
                  .dark .input-mystic:focus {
                    border-color: oklch(0.82 0.14 85 / 0.7);
                    background: oklch(0.18 0.008 85 / 0.72);
                  }
                `}</style>
                </form>
              </>
            ) : (
              <div className="flex items-center gap-2 w-full">
                <button
                  type="button"
                  onClick={() => setFormExpanded(true)}
                  className="group flex flex-1 items-center gap-2 sm:gap-3 rounded-full border border-gold-soft bg-[#FAF6F0] dark:bg-card/45 px-4 sm:px-5 py-2.5 text-left backdrop-blur transition hover:border-primary/60 overflow-hidden shadow-sm"
                >
                  <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 animate-pulse" />
                  <span className="font-serif-soft text-base italic text-gold-gradient truncate flex items-center gap-1.5">
                    <span>{form.name}</span>
                    <span className="text-[11px] sm:text-[13px] text-muted-foreground font-sans not-italic truncate">
                      · {formatShortDate(form.date)} (DL)
                    </span>
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[#8A8077] transition group-hover:text-primary shrink-0">
                    <Pencil className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sửa</span>
                  </span>
                </button>
                {apiChart && (
                  <div className="hidden md:flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-primary/60 bg-primary/10 text-primary transition hover:bg-primary/20"
                      aria-label="Chia sẻ lá số"
                      title="Chia sẻ lá số"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={deleteCurrentChart}
                      disabled={loading}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-destructive/50 bg-destructive/10 text-destructive transition hover:bg-destructive/20 disabled:opacity-50"
                      aria-label="Xóa lá số hiện tại"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* User hints */}
            {!opened && (
              <div className="flex items-center justify-center gap-2 rounded-full border border-gold-soft/60 bg-background/30 px-4 py-2 text-center text-[11px] italic text-muted-foreground backdrop-blur">
                <Sparkles className="h-3 w-3 text-primary" />
                <span>
                  Mẹo: Bấm{" "}
                  <span className="font-display tracking-wider text-primary">"KHAI MỞ LÁ SỐ"</span>{" "}
                  ở Thiên Bàn (ô giữa) để bắt đầu luận giải.
                </span>
              </div>
            )}
            {opened && !pinnedCung && (
              <div className="hidden md:flex flex-wrap items-center justify-center gap-x-5 gap-y-1 rounded-full border border-gold-soft/60 bg-background/30 px-4 py-2 text-center text-[11px] italic text-muted-foreground backdrop-blur">
                <span className="hidden lg:inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-primary" />
                  Rê chuột lên một cung để xem nhanh
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Pin className="h-3 w-3 text-primary" />
                  Bấm vào cung để <span className="text-primary">ghim &amp; luận sâu</span>
                </span>
                <span className="hidden sm:inline-flex items-center gap-1.5">
                  <Pencil className="h-3 w-3 text-primary" />
                  Bấm thanh trên cùng để sửa thông tin
                </span>
              </div>
            )}
          </div>

          {/* Chart + reading */}
          <div ref={mobileWorkspaceRef} className="scroll-mt-2">
            {apiChart && (
              <div className="relative z-30 mb-3 rounded-2xl border border-gold-soft/40 bg-[#FAF6F0] dark:bg-card/40 p-1 shadow-sm backdrop-blur-xl lg:hidden">
                <div className="flex min-w-0 flex-1 rounded-xl p-0.5 gap-1.5">
                  <button
                    type="button"
                    onClick={() => switchMobilePane("chart")}
                    aria-pressed={mobilePane === "chart"}
                    className={`flex-1 flex h-10 items-center justify-center gap-2 rounded-xl text-sm font-semibold font-serif-soft transition duration-300 ${
                      mobilePane === "chart"
                        ? "bg-white dark:bg-zinc-800 text-[#3E2723] dark:text-[#E5B869] shadow-sm border border-gold-soft/20"
                        : "text-[#8A8077] dark:text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Sparkles className="h-4 w-4 text-primary" />
                    Lá số
                  </button>
                  <button
                    type="button"
                    onClick={() => switchMobilePane("reading")}
                    disabled={!opened}
                    aria-pressed={mobilePane === "reading"}
                    className={`relative flex-1 flex h-10 items-center justify-center gap-2 rounded-xl text-sm font-semibold font-serif-soft transition duration-300 disabled:cursor-not-allowed disabled:opacity-40 ${
                      mobilePane === "reading"
                        ? "bg-white dark:bg-zinc-800 text-[#3E2723] dark:text-[#E5B869] shadow-sm border border-gold-soft/20"
                        : "text-[#8A8077] dark:text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <ScrollText className="h-4 w-4 text-primary" />
                    Luận giải
                    {readingLoading && (
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                    )}
                  </button>
                </div>

                <div
                  className="mt-1 grid grid-cols-[1.2fr_1fr_1fr_2.5rem] gap-1 border-t border-gold-soft/30 pt-1"
                  aria-label="Tác vụ lá số"
                >
                  <button
                    type="button"
                    onClick={() => setChartsDrawerOpen(true)}
                    className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15"
                  >
                    <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">Lá số đã lập</span>
                  </button>
                  <button
                    type="button"
                    disabled={!opened}
                    onClick={() => setShowOverviewChat(true)}
                    className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <MessageSquare className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">Đàm đạo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15"
                  >
                    <Share2 className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">Chia sẻ</span>
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => void deleteCurrentChart()}
                    className="flex h-10 w-10 items-center justify-center rounded-xl text-destructive transition hover:bg-destructive/10 active:bg-destructive/15 disabled:opacity-50"
                    aria-label="Xóa lá số"
                    title="Xóa lá số"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {apiChart ? (
              <div
                className={`grid grid-cols-1 gap-4 md:gap-6 ${
                  opened
                    ? "lg:grid-cols-[3fr_2fr]"
                    : "lg:grid-cols-[minmax(0,7fr)_minmax(300px,3fr)]"
                }`}
              >
                {/* 12-cung chart */}
                <div
                  className={`w-full pb-4 ${mobilePane === "reading" ? "hidden lg:block" : "block"}`}
                >
                  {/* Mode switch header */}
                  <div className="hidden md:flex items-center justify-between mb-4 px-2">
                    <span className="font-display text-[9px] tracking-[0.4em] text-primary/80 mobile-label-uppercase">
                      PHƯƠNG THỨC XEM
                    </span>
                    <div className="flex rounded-full border border-primary/20 p-0.5 bg-background/50 backdrop-blur shadow-inner">
                      <button
                        type="button"
                        onClick={() => setViewMode("list")}
                        className={`px-4 py-2 text-xs rounded-full font-serif-soft transition-all duration-300 ${
                          viewMode === "list"
                            ? "bg-primary text-background font-semibold shadow-md"
                            : "text-primary/70 hover:text-primary"
                        }`}
                      >
                        Danh sách
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode("grid")}
                        className={`px-4 py-2 text-xs rounded-full font-serif-soft transition-all duration-300 ${
                          viewMode === "grid"
                            ? "bg-primary text-background font-semibold shadow-md"
                            : "text-primary/70 hover:text-primary"
                        }`}
                      >
                        Lưới
                      </button>
                    </div>
                  </div>

                  {opened && (
                    <div className="hidden md:flex mb-4 mx-2 p-3 rounded-2xl border border-primary/25 bg-primary/5 text-[11px] sm:text-xs text-primary/95 items-start sm:items-center gap-2 shadow-sm animate-pulse">
                      <span className="text-sm shrink-0">👉</span>
                      <span>
                        <b>Xem chi tiết cung:</b> Nhấp trực tiếp vào bất kỳ cung nào dưới đây (Mệnh,
                        Phu Thê, Tài Bạch...) để xem luận giải chi tiết &amp; hỏi thầy về cung đó.
                      </span>
                    </div>
                  )}

                  {viewMode === "grid" ? (
                    <div>
                      <div className="lg:hidden mb-2 flex justify-end px-2">
                        <button
                          type="button"
                          onClick={() => setChartZoomed((z) => !z)}
                          aria-label={chartZoomed ? "Thu về vừa màn hình" : "Phóng to lá số"}
                          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-primary/40 bg-background/60 px-3.5 text-xs font-medium text-primary backdrop-blur transition active:scale-95"
                        >
                          {chartZoomed ? (
                            <>
                              <Minimize2 className="h-3.5 w-3.5" /> Vừa màn hình
                            </>
                          ) : (
                            <>
                              <Maximize2 className="h-3.5 w-3.5" /> Phóng to
                            </>
                          )}
                        </button>
                      </div>
                      <div
                        ref={chartViewportRef}
                        style={{ height: chartHeight, maxHeight: chartZoomed ? "72vh" : undefined }}
                        className={`w-full lg:h-auto! lg:max-h-none! lg:overflow-visible ${chartZoomed ? "overflow-auto scrollbar-thin" : "overflow-hidden"}`}
                      >
                        <div
                          style={{ transform: `scale(${chartScale})`, transformOrigin: "top left" }}
                          className="lg:transform-none!"
                        >
                          <section
                            ref={chartInnerRef}
                            key={`chart-${chartKey}`}
                            className={`relative min-w-[640px] lg:min-w-0 rounded-3xl border border-primary/20 bg-card/40 p-3 md:p-4 shadow-mystic backdrop-blur ${chartKey > 0 ? "frame-sweep" : ""}`}
                          >
                            <div className="relative grid grid-cols-4 grid-rows-[repeat(4,minmax(140px,auto))] gap-1.5 md:gap-2">
                              {chart.cungs.map((c, i) => {
                                const isFocus = activeCung === c.cung;
                                const isPinned = pinnedCung === c.cung;
                                const dim = !!activeCung && !isFocus;
                                const cungScore = apiChart?.scores?.cungs.find(
                                  (sc) => sc.key === TUVI_CUNG_KEY_BY_NAME[c.cung],
                                )?.score;
                                return (
                                  <CungCell
                                    key={c.chi}
                                    data={c}
                                    cellIndex={i}
                                    chartKey={chartKey}
                                    flashKey={flashKey}
                                    focus={isFocus}
                                    pinned={isPinned}
                                    dim={dim}
                                    score={cungScore}
                                    onHoverIn={() =>
                                      opened && !pinnedCung && setHoveredCung(c.cung)
                                    }
                                    onHoverOut={() =>
                                      setHoveredCung((prev) => (prev === c.cung ? null : prev))
                                    }
                                    onClick={() => {
                                      if (!opened) {
                                        handleOpen();
                                        return;
                                      }
                                      setPinnedCung((prev) => {
                                        const next = prev === c.cung ? null : c.cung;
                                        if (next) setIsCungCollapsed(false);
                                        return next;
                                      });
                                      setHoveredCung(null);
                                    }}
                                  />
                                );
                              })}

                              {/* Tam Hợp / Xung Chiếu laser overlay */}
                              {activeCung &&
                                (() => {
                                  const src = chart.cungs.find((c) => c.cung === activeCung);
                                  if (!src) return null;
                                  const start = cellCenter(src.row, src.col);
                                  const trineChi = TAM_HOP[src.chi] || [];
                                  const opposeChi = XUNG_CHIEU[src.chi];
                                  const trineCells = trineChi
                                    .map((chi) => chart.cungs.find((c) => c.chi === chi))
                                    .filter(Boolean) as CungData[];
                                  const opposeCell = chart.cungs.find((c) => c.chi === opposeChi);
                                  return (
                                    <svg
                                      key={`lasers-${activeCung}`}
                                      className="pointer-events-none absolute inset-0 z-10"
                                      viewBox="0 0 100 100"
                                      preserveAspectRatio="none"
                                    >
                                      {trineCells.map((tc) => {
                                        const e = cellCenter(tc.row, tc.col);
                                        return (
                                          <line
                                            key={`trine-${tc.chi}`}
                                            x1={start.x}
                                            y1={start.y}
                                            x2={e.x}
                                            y2={e.y}
                                            className="laser-line laser-line-trine"
                                            vectorEffect="non-scaling-stroke"
                                          />
                                        );
                                      })}
                                      {opposeCell && (
                                        <line
                                          x1={start.x}
                                          y1={start.y}
                                          x2={cellCenter(opposeCell.row, opposeCell.col).x}
                                          y2={cellCenter(opposeCell.row, opposeCell.col).y}
                                          className="laser-line laser-line-oppose"
                                          vectorEffect="non-scaling-stroke"
                                        />
                                      )}
                                      {/* nodes */}
                                      {[
                                        src,
                                        ...trineCells,
                                        ...(opposeCell ? [opposeCell] : []),
                                      ].map((n) => {
                                        const p = cellCenter(n.row, n.col);
                                        return (
                                          <circle
                                            key={`node-${n.chi}`}
                                            cx={p.x}
                                            cy={p.y}
                                            r={0.6}
                                            className="laser-node"
                                          />
                                        );
                                      })}
                                    </svg>
                                  );
                                })()}

                              {/* Cosmic ripple from Khai Mở */}
                              {rippleKey > 0 && (
                                <div key={`ripple-${rippleKey}`} className="ripple-shockwave" />
                              )}

                              {/* Thiên bàn center 2x2 */}
                              <div
                                key={`tb-${flashKey}`}
                                className={`col-start-2 col-end-4 row-start-2 row-end-4 flex flex-col items-center justify-between rounded-xl border border-gold-soft/45 bg-card/80 stardust p-5 text-center shadow-[0_14px_34px_rgba(92,64,36,0.08)] backdrop-blur-sm dark:border-primary/20 dark:bg-background/30 dark:shadow-none ${opened ? "thien-ban-glow" : ""} ${activeCung ? "cung-dim" : ""}`}
                              >
                                {/* Top: title */}
                                <div className="flex flex-col items-center gap-1">
                                  <span className="font-display text-[9px] tracking-[0.5em] text-primary/80">
                                    THIÊN BÀN
                                  </span>
                                  <h3 className="font-serif-soft text-2xl italic text-gold-gradient leading-none">
                                    LÁ SỐ TỬ VI
                                  </h3>
                                  <div className="h-px w-16 bg-primary/40" />
                                </div>

                                {/* Middle: detailed info table */}
                                <div className="flex flex-1 w-full flex-col gap-0.5 px-2 py-3 text-left text-[12px]">
                                  <InfoRow
                                    label="Họ tên"
                                    value={shareMaskActive ? "Ẩn danh" : form.name}
                                    bold
                                  />
                                  <InfoRow
                                    label="Năm (Âm lịch)"
                                    value={
                                      shareMaskActive
                                        ? "•••"
                                        : `${chart.apiMeta?.namAm ?? (parsedDate ? parsedDate.year : "")}`
                                    }
                                    extra={
                                      shareMaskActive
                                        ? undefined
                                        : formatCanChi(chart.apiMeta?.canNam, chart.apiMeta?.chiNam)
                                    }
                                  />
                                  <InfoRow
                                    label="Tháng (Âm lịch)"
                                    value={
                                      shareMaskActive
                                        ? "•••"
                                        : `${chart.apiMeta?.thangAm ?? (parsedDate ? parsedDate.month : "")}`
                                    }
                                    extra={
                                      shareMaskActive
                                        ? undefined
                                        : formatCanChi(
                                            chart.apiMeta?.canThang,
                                            chart.apiMeta?.chiThang,
                                          )
                                    }
                                  />
                                  <InfoRow
                                    label="Ngày (Âm lịch)"
                                    value={
                                      shareMaskActive
                                        ? "•••"
                                        : `${chart.apiMeta?.ngayAm ?? (parsedDate ? parsedDate.day : "")}`
                                    }
                                    extra={
                                      shareMaskActive
                                        ? undefined
                                        : formatCanChi(
                                            chart.apiMeta?.canNgay,
                                            chart.apiMeta?.chiNgay,
                                          )
                                    }
                                  />
                                  <InfoRow
                                    label="Giờ"
                                    value={shareMaskActive ? "•••" : formatHour(form.time)}
                                    extra={
                                      shareMaskActive
                                        ? undefined
                                        : formatCanChi(chart.apiMeta?.canGio, chart.apiMeta?.chiGio)
                                    }
                                  />
                                  {luuNien ? (
                                    <InfoRow
                                      label="Năm xem (sao lưu)"
                                      value={`${luuNien.can_chi} (${luuNien.year})`}
                                    />
                                  ) : (
                                    formatNamXem(chart.apiMeta) && (
                                      <InfoRow
                                        label="Năm xem"
                                        value={formatNamXem(chart.apiMeta)!}
                                      />
                                    )
                                  )}
                                  {chart.apiMeta?.amDuong && (
                                    <InfoRow label="Âm dương" value={chart.apiMeta.amDuong} />
                                  )}
                                  <InfoRow label="Bản mệnh" value={chart.nguHanh} accent />
                                  <InfoRow
                                    label="Mệnh cục"
                                    value={`${chart.cuc}${chart.apiMeta?.soCuc ? ` (${chart.apiMeta.soCuc})` : ""}`}
                                    extra={chart.apiMeta?.menhCucRelation}
                                    accent
                                  />
                                  <InfoRow
                                    label="Cân lượng"
                                    value={chart.apiMeta?.canLuong ?? "—"}
                                  />
                                  {chart.apiMeta?.chuMenh && (
                                    <InfoRow label="Chủ mệnh" value={chart.apiMeta.chuMenh} />
                                  )}
                                  {chart.apiMeta?.chuThan && (
                                    <InfoRow label="Chủ thân" value={chart.apiMeta.chuThan} />
                                  )}
                                  {chart.apiMeta?.laiNhanCung && (
                                    <InfoRow
                                      label="Lai nhân cung"
                                      value={chart.apiMeta.laiNhanCung}
                                    />
                                  )}
                                </div>

                                {/* Bottom: CTA — flat, slim, anchored */}
                                {!opened ? (
                                  <button
                                    data-share-exclude
                                    data-tour="tuvi-khaimo"
                                    onClick={handleOpen}
                                    className="group relative hidden items-center gap-2 rounded-full border border-primary/60 bg-primary/10 px-5 py-1.5 backdrop-blur-sm transition hover:border-primary hover:bg-primary/20 lg:inline-flex"
                                  >
                                    <span className="absolute inset-[-20px] -z-10 rounded-full bg-primary/15 blur-3xl" />
                                    <Sparkles className="h-3 w-3 text-primary" />
                                    <span className="font-serif-soft text-xs italic tracking-[0.3em] text-primary">
                                      KHAI MỞ LÁ SỐ
                                    </span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    data-share-exclude
                                    data-tour="tuvi-khaimo"
                                    onClick={handleReopen}
                                    className="group relative hidden items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-4 py-1 backdrop-blur-sm transition hover:border-primary/60 hover:bg-primary/10 active:scale-95 text-primary/80 hover:text-primary animate-glow lg:inline-flex"
                                  >
                                    <span className="font-serif-soft text-[10px] italic tracking-[0.25em]">
                                      ✦ KHAI MỞ LẠI ĐẠI CỤC ✦
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Legend strip */}
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-primary/15 pt-2 text-[10px] text-foreground/70">
                              <div className="flex flex-wrap gap-x-3">
                                <span>
                                  <b className="text-primary">M</b>: Miếu
                                </span>
                                <span>
                                  <b className="text-primary">V</b>: Vượng
                                </span>
                                <span>
                                  <b className="text-primary">Đ</b>: Đắc
                                </span>
                                <span>
                                  <b className="text-primary">B</b>: Bình hòa
                                </span>
                                <span>
                                  <b className="text-primary">H</b>: Hãm
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-x-3">
                                <span className="inline-flex items-center gap-1">
                                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-metal/60" />
                                  Kim
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-wood/80" />
                                  Mộc
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-water/80" />
                                  Thủy
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-fire/80" />
                                  Hỏa
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <i className="inline-block h-2.5 w-2.5 rounded-sm bg-element-earth/80" />
                                  Thổ
                                </span>
                              </div>
                            </div>
                          </section>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full flex flex-col gap-4">
                      {/* Thiên bàn card at top of list */}
                      <div
                        key={`tb-list-${flashKey}`}
                        className={`w-full flex flex-col items-center justify-between rounded-3xl border border-gold-soft/45 bg-card/80 stardust p-6 text-center shadow-[0_14px_34px_rgba(92,64,36,0.08)] backdrop-blur-sm dark:border-primary/20 dark:bg-card/40 dark:shadow-none ${opened ? "thien-ban-glow" : ""}`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-display text-[9px] tracking-[0.5em] text-primary/80">
                            THIÊN BÀN
                          </span>
                          <h3 className="font-serif-soft text-2xl italic text-gold-gradient leading-none">
                            LÁ SỐ TỬ VI
                          </h3>
                          <div className="h-px w-16 bg-primary/40" />
                        </div>

                        <div className="flex flex-1 w-full max-w-md flex-col gap-0.5 px-2 py-4 text-left text-[12px] mx-auto">
                          <InfoRow label="Họ tên" value={form.name} bold />
                          <InfoRow
                            label="Năm (Âm lịch)"
                            value={`${chart.apiMeta?.namAm ?? (parsedDate ? parsedDate.year : "")}`}
                            extra={formatCanChi(chart.apiMeta?.canNam, chart.apiMeta?.chiNam)}
                          />
                          <InfoRow
                            label="Tháng (Âm lịch)"
                            value={`${chart.apiMeta?.thangAm ?? (parsedDate ? parsedDate.month : "")}`}
                            extra={formatCanChi(chart.apiMeta?.canThang, chart.apiMeta?.chiThang)}
                          />
                          <InfoRow
                            label="Ngày (Âm lịch)"
                            value={`${chart.apiMeta?.ngayAm ?? (parsedDate ? parsedDate.day : "")}`}
                            extra={formatCanChi(chart.apiMeta?.canNgay, chart.apiMeta?.chiNgay)}
                          />
                          <InfoRow
                            label="Giờ"
                            value={formatHour(form.time)}
                            extra={formatCanChi(chart.apiMeta?.canGio, chart.apiMeta?.chiGio)}
                          />
                          {formatNamXem(chart.apiMeta) && (
                            <InfoRow label="Năm xem" value={formatNamXem(chart.apiMeta)!} />
                          )}
                          {chart.apiMeta?.amDuong && (
                            <InfoRow label="Âm dương" value={chart.apiMeta.amDuong} />
                          )}
                          <InfoRow label="Bản mệnh" value={chart.nguHanh} accent />
                          <InfoRow
                            label="Mệnh cục"
                            value={`${chart.cuc}${chart.apiMeta?.soCuc ? ` (${chart.apiMeta.soCuc})` : ""}`}
                            extra={chart.apiMeta?.menhCucRelation}
                            accent
                          />
                          <InfoRow label="Cân lượng" value={chart.apiMeta?.canLuong ?? "—"} />
                          {chart.apiMeta?.chuMenh && (
                            <InfoRow label="Chủ mệnh" value={chart.apiMeta.chuMenh} />
                          )}
                          {chart.apiMeta?.chuThan && (
                            <InfoRow label="Chủ thân" value={chart.apiMeta.chuThan} />
                          )}
                          {chart.apiMeta?.laiNhanCung && (
                            <InfoRow label="Lai nhân cung" value={chart.apiMeta.laiNhanCung} />
                          )}
                        </div>

                        {!opened ? (
                          <button
                            data-tour="tuvi-khaimo"
                            onClick={handleOpen}
                            className="group relative inline-flex items-center gap-2 rounded-full border border-primary/60 bg-primary/10 px-6 py-2 backdrop-blur-sm transition hover:border-primary hover:bg-primary/20"
                          >
                            <span className="absolute inset-[-20px] -z-10 rounded-full bg-primary/15 blur-3xl" />
                            <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
                            <span className="font-serif-soft text-xs italic tracking-[0.3em] text-primary">
                              KHAI MỞ LÁ SỐ
                            </span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-tour="tuvi-khaimo"
                            onClick={handleReopen}
                            className="group relative inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 backdrop-blur-sm transition hover:border-primary/60 hover:bg-primary/10 active:scale-95 text-primary/80 hover:text-primary animate-glow"
                          >
                            <span className="font-serif-soft text-xs italic tracking-[0.25em]">
                              ✦ KHAI MỞ LẠI ĐẠI CỤC ✦
                            </span>
                          </button>
                        )}
                      </div>

                      {/* 12-cung vertical list/responsive grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {sortedCungs.map((c, i) => {
                          const isFocus = activeCung === c.cung;
                          const isPinned = pinnedCung === c.cung;
                          const dim = !!activeCung && !isFocus;
                          const originalIndex = chart.cungs.findIndex((item) => item.chi === c.chi);
                          const cungScore = apiChart?.scores?.cungs.find(
                            (sc) => sc.key === TUVI_CUNG_KEY_BY_NAME[c.cung],
                          )?.score;
                          return (
                            <CungCell
                              key={c.chi}
                              data={c}
                              cellIndex={originalIndex !== -1 ? originalIndex : i}
                              chartKey={chartKey}
                              flashKey={flashKey}
                              focus={isFocus}
                              pinned={isPinned}
                              dim={dim}
                              score={cungScore}
                              onHoverIn={() => opened && !pinnedCung && setHoveredCung(c.cung)}
                              onHoverOut={() =>
                                setHoveredCung((prev) => (prev === c.cung ? null : prev))
                              }
                              onClick={() => {
                                if (!opened) {
                                  handleOpen();
                                  return;
                                }
                                setPinnedCung((prev) => {
                                  const next = prev === c.cung ? null : c.cung;
                                  if (next) setIsCungCollapsed(false);
                                  return next;
                                });
                                setHoveredCung(null);
                              }}
                              isListView={true}
                            />
                          );
                        })}
                      </div>

                      {/* Legend strip list view */}
                      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-primary/15 pt-3 text-[10px] text-foreground/70">
                        <div className="flex flex-wrap gap-x-3">
                          <span>
                            <b className="text-primary">M</b>: Miếu
                          </span>
                          <span>
                            <b className="text-primary">V</b>: Vượng
                          </span>
                          <span>
                            <b className="text-primary">Đ</b>: Đắc
                          </span>
                          <span>
                            <b className="text-primary">B</b>: Bình hòa
                          </span>
                          <span>
                            <b className="text-primary">H</b>: Hãm
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3">
                          <span className="inline-flex items-center gap-1">
                            <i className="inline-block h-2 w-2 rounded-sm bg-element-metal/60" />{" "}
                            Kim
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="inline-block h-2 w-2 rounded-sm bg-element-wood/80" /> Mộc
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="inline-block h-2 w-2 rounded-sm bg-element-water/80" />{" "}
                            Thủy
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="inline-block h-2 w-2 rounded-sm bg-element-fire/80" /> Hỏa
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="inline-block h-2 w-2 rounded-sm bg-element-earth/80" />{" "}
                            Thổ
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right column: layered cards */}
                <div
                  className={`relative w-full ${mobilePane === "chart" ? "hidden lg:block" : "block"}`}
                >
                  <aside
                    ref={readingRef}
                    data-tour="tuvi-reading"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    className={`relative z-10 flex h-auto flex-col p-2 sm:p-5 lg:sticky lg:top-2 lg:p-2 ${
                      opened
                        ? "lg:h-[calc(100dvh-12.5rem)] lg:min-h-[480px] lg:max-h-[900px]"
                        : "lg:min-h-[620px]"
                    }`}
                  >
                    <div className="silk-paper absolute inset-0 rounded-3xl border border-gold-soft pointer-events-none -z-10 hidden lg:block" />
                    {!opened ? (
                      <PreReadingSummary chart={chart} name={form.name} onOpen={handleOpen} />
                    ) : (
                      <>
                        <nav
                          aria-label="Chọn phần luận giải"
                          data-tour="tuvi-cung-nav"
                          className="scrollbar-none relative z-30 -mx-1 mb-2 flex h-12 shrink-0 snap-x items-center gap-2 overflow-x-auto px-1 lg:hidden"
                        >
                          <button
                            ref={(element) => {
                              mobileReadingNavRefs.current.__overview = element;
                            }}
                            type="button"
                            onClick={() => selectMobileReading(null)}
                            aria-pressed={!pinnedCung}
                            className={`h-10 shrink-0 snap-center rounded-full border px-4 text-sm font-medium transition ${
                              !pinnedCung
                                ? "border-primary bg-primary text-primary-foreground shadow-gold"
                                : "border-gold-soft/60 bg-background/70 text-muted-foreground"
                            }`}
                          >
                            Đại cục
                          </button>
                          {CUNG_NAMES.map((cung) => (
                            <button
                              key={cung}
                              ref={(element) => {
                                mobileReadingNavRefs.current[cung] = element;
                              }}
                              type="button"
                              onClick={() => selectMobileReading(cung)}
                              aria-pressed={pinnedCung === cung}
                              className={`h-10 shrink-0 snap-center rounded-full border px-4 text-sm font-medium transition ${
                                pinnedCung === cung
                                  ? "border-primary bg-primary text-primary-foreground shadow-gold"
                                  : "border-gold-soft/60 bg-background/70 text-muted-foreground"
                              }`}
                            >
                              {cung}
                            </button>
                          ))}
                        </nav>

                        {/* Base: Tổng Quan Đại Cục — luôn mounted khi opened */}
                        <div
                          className={`${activeCung ? "hidden lg:block card-receded" : "card-front"} min-h-0 flex-1 w-full ${showOverviewChat ? "h-full" : "h-auto"}`}
                        >
                          <div className="h-full">
                            <div className="flex h-full min-h-0 flex-col gap-4">
                              <div
                                className={`${isOverviewCollapsed ? "h-auto shrink-0" : "flex-1"} min-h-0 border-none bg-transparent lg:border lg:border-gold-soft/50 lg:bg-background/20 p-2 sm:p-6 transition-all duration-300 flex flex-col`}
                              >
                                <div className="flex h-full flex-col">
                                  <div
                                    data-tour="tuvi-reading-anchor"
                                    className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shrink-0"
                                  >
                                    <div className="min-w-0">
                                      <span className="font-display text-[10px] tracking-[0.4em] text-primary mobile-label-uppercase">
                                        ĐẠI CỤC
                                      </span>
                                      <div className="mt-1.5 h-px w-24 bg-gold-gradient" />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 justify-start sm:justify-end">
                                      {reading && !readingLoading && (
                                        <button
                                          type="button"
                                          onClick={() => void revealReading(true)}
                                          className="inline-flex h-9 items-center rounded-full border border-gold-soft/60 bg-background/40 px-3 text-xs text-muted-foreground transition hover:border-primary/60 hover:text-primary whitespace-nowrap sm:h-auto sm:px-2.5 sm:py-1 sm:text-[10px]"
                                        >
                                          Khai mở lại
                                        </button>
                                      )}
                                      {reading && !readingLoading && (
                                        <button
                                          type="button"
                                          onClick={() => setShowOverviewChat(true)}
                                          className="flex h-9 items-center gap-1.5 rounded-full border border-[#E5B869]/50 bg-[#E5B869]/10 px-3 text-xs font-medium text-[#E5B869] transition hover:bg-[#E5B869]/20 whitespace-nowrap sm:h-auto sm:px-2.5 sm:py-1 sm:text-[10px] sm:font-normal"
                                        >
                                          <MessageSquare className="h-3 w-3" /> Đàm đạo
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setIsOverviewCollapsed((current) => !current)
                                        }
                                        className="hidden rounded-full border border-gold-soft/60 bg-background/40 px-2.5 py-1 text-[10px] text-muted-foreground transition hover:border-primary/60 hover:text-primary whitespace-nowrap sm:inline-flex"
                                      >
                                        {isOverviewCollapsed ? "Mở rộng" : "Thu gọn"}
                                      </button>
                                    </div>
                                  </div>
                                  <div
                                    className={`${isOverviewCollapsed ? "hidden" : ""} scrollbar-none mt-5 flex-1 overflow-y-visible lg:overflow-y-auto font-serif-soft text-[15px] leading-relaxed text-foreground/95`}
                                  >
                                    {apiChart?.scores && (
                                      <TuviHuyenKhi scores={apiChart.scores} className="mb-6" />
                                    )}
                                    {reading?.dai_cuc ? (
                                      <div>
                                        <ReactMarkdown
                                          remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                                          components={MYSTIC_MD_COMPONENTS}
                                        >
                                          {reading.dai_cuc}
                                        </ReactMarkdown>
                                        <AiWatermark />
                                        {reading.cungs_locked ? (
                                          <CungsPaywall
                                            price={reading.cungs_price ?? 0}
                                            loading={cungsUnlocking}
                                            onUnlock={() => void unlockCungs()}
                                          />
                                        ) : readingLoading ? (
                                          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                                            <YinYang className="w-4 h-4 text-primary animate-bagua" />
                                            <span>Đang luận 12 cung…</span>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-3 text-center text-xs text-primary/90 animate-pulse">
                                              ✨ <b>Mẹo xem tiếp:</b>{" "}
                                              <span className="lg:hidden">
                                                Chọn một cung ở thanh phía trên để xem luận giải chi
                                                tiết ngay tại đây.
                                              </span>
                                              <span className="hidden lg:inline">
                                                Bấm trực tiếp vào các cung trên lá số (ví dụ: cung
                                                Mệnh, Phu Thê, Tài Bạch...) để xem luận giải chi
                                                tiết từng cung và đàm đạo thêm với Cổ Nhân.
                                              </span>
                                            </div>
                                            <div className="mt-3 text-center">
                                              <button
                                                type="button"
                                                disabled={cungsUnlocking}
                                                onClick={() => void unlockCungs(true)}
                                                className="text-[11px] italic text-muted-foreground underline-offset-2 transition hover:text-primary hover:underline disabled:opacity-50"
                                              >
                                                Muốn luận lại từ đầu? Khai mở mới 12 cung (10.000đ)
                                              </button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    ) : readingError ? (
                                      <div className="text-sm text-destructive">
                                        <strong>Không khai mở được lá số:</strong> {readingError}
                                      </div>
                                    ) : readingLoading ? (
                                      <div className="flex h-full items-center justify-center">
                                        <YinYang className="w-8 h-8 text-primary animate-bagua" />
                                      </div>
                                    ) : (
                                      <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
                                        <YinYang className="w-12 h-12 text-gold-gradient animate-bagua" />
                                        <p className="mt-4 text-sm italic">
                                          Bấm Khai Mở để AI luận giải toàn bộ lá số. Sau đó click
                                          từng cung trên lá số để xem chi tiết.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {pinnedCung &&
                          (() => {
                            const cungKey = TUVI_CUNG_KEY_BY_NAME[pinnedCung];
                            const cungReading = cungKey
                              ? reading?.cungs.find((c) => c.key === cungKey)
                              : undefined;
                            const cungScore = cungKey
                              ? apiChart?.scores?.cungs.find((c) => c.key === cungKey)?.score
                              : undefined;
                            return (
                              <div className="relative lg:absolute w-full lg:w-auto h-auto lg:h-auto inset-x-0 lg:bottom-0 lg:top-16 p-0 lg:p-0 animate-card-rise">
                                <div className="relative flex h-auto lg:h-full w-full flex-col border-none bg-transparent lg:border lg:border-gold-soft/50 lg:bg-background/95 p-2 sm:p-5 lg:backdrop-blur-xl lg:shadow-mystic">
                                  <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shrink-0">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-display text-[10px] tracking-[0.3em] sm:tracking-[0.4em] text-primary break-words">
                                          {cungReading?.label || `CUNG ${pinnedCung.toUpperCase()}`}
                                        </span>
                                        {typeof cungScore === "number" && (
                                          <CungScoreBadge
                                            score={cungScore}
                                            bandKey={bandKeyForScore(cungScore)}
                                          />
                                        )}
                                      </div>
                                      <div className="mt-1.5 h-px w-24 bg-gold-gradient" />
                                    </div>
                                    <div className="flex items-center gap-1.5 justify-start sm:justify-end shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => setShowCungChat(true)}
                                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#E5B869]/50 bg-[#E5B869]/10 px-3 text-xs font-medium text-[#E5B869] transition hover:bg-[#E5B869]/20 whitespace-nowrap sm:h-auto sm:px-2.5 sm:py-1 sm:text-[10px] sm:font-normal"
                                      >
                                        <MessageSquare className="h-3 w-3" /> Đàm đạo
                                      </button>
                                      {cungReading?.content_md && (
                                        <button
                                          type="button"
                                          onClick={() => setIsCungCollapsed((current) => !current)}
                                          className="hidden items-center gap-1 rounded-full border border-gold-soft bg-background/60 px-2.5 py-1 text-[10px] text-muted-foreground hover:text-primary transition whitespace-nowrap sm:inline-flex"
                                        >
                                          {isCungCollapsed ? "Mở rộng" : "Thu gọn"}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => {
                                          selectMobileReading(null);
                                        }}
                                        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-gold-soft bg-background/60 px-3 text-xs text-muted-foreground hover:text-primary whitespace-nowrap sm:h-auto sm:px-2.5 sm:py-1 sm:text-[10px]"
                                      >
                                        <ArrowLeft className="h-3.5 w-3.5 sm:hidden" />
                                        <Pin className="hidden h-3 w-3 sm:block" />
                                        <span className="sm:hidden">Đại cục</span>
                                        <span className="hidden sm:inline">Hủy ghim</span>
                                      </button>
                                    </div>
                                  </div>
                                  <div
                                    className={`${isCungCollapsed ? "hidden" : "flex-1"} scrollbar-none mb-3 min-h-0 overflow-y-visible lg:overflow-y-auto font-serif-soft text-[15px] leading-relaxed text-foreground/95`}
                                  >
                                    {cungReading?.content_md ? (
                                      <div>
                                        <ReactMarkdown
                                          remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                                          components={MYSTIC_MD_COMPONENTS}
                                        >
                                          {cungReading.content_md}
                                        </ReactMarkdown>
                                        <AiWatermark />
                                      </div>
                                    ) : reading?.cungs_locked ? (
                                      <CungsPaywall
                                        price={reading.cungs_price ?? 0}
                                        loading={cungsUnlocking}
                                        onUnlock={() => void unlockCungs()}
                                      />
                                    ) : readingLoading ? (
                                      <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                                        <YinYang className="h-7 w-7 animate-bagua text-primary" />
                                        <p className="text-sm italic">
                                          Đang luận giải cung {pinnedCung}…
                                        </p>
                                      </div>
                                    ) : readingError ? (
                                      <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-4 text-sm text-destructive">
                                        <strong>Không tải được luận giải:</strong> {readingError}
                                      </div>
                                    ) : (
                                      <p className="text-sm italic text-muted-foreground">
                                        Cung này chưa có luận giải. Bấm <strong>Khai mở</strong> ở
                                        khung Đại cục để AI luận toàn bộ lá số trước.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                      </>
                    )}
                  </aside>
                </div>
              </div>
            ) : (
              <section className="rounded-3xl border border-dashed border-gold-soft bg-card/30 px-5 py-10 text-center backdrop-blur md:px-8 md:py-14">
                <div className="mx-auto flex max-w-xl flex-col items-center">
                  <ScrollText className="h-10 w-10 text-primary/55" />
                  <h2 className="mt-4 font-serif-soft text-2xl italic text-gold-gradient">
                    Lập lá số từ thông tin của bạn
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Điền họ tên, ngày sinh và giờ sinh ở biểu mẫu phía trên. Bàn lá số chỉ xuất hiện
                    sau khi bạn chủ động xác nhận thông tin.
                  </p>
                </div>
              </section>
            )}

            {/* Đại Vận 10 năm */}
            {apiChart && chart.daiVan && chart.daiVan.length > 0 && (
              <section
                className={`rounded-2xl border border-gold-soft bg-card/40 p-5 backdrop-blur ${mobilePane === "reading" ? "hidden lg:block" : "block"}`}
              >
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="font-display text-[10px] tracking-[0.4em] text-primary mobile-label-uppercase">
                    ĐẠI VẬN — 10 NĂM
                  </span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 sm:overflow-visible sm:pb-0 scrollbar-none">
                  {chart.daiVan.map((dv) => (
                    <div
                      key={`${dv.so_thu}-${dv.chi}`}
                      className="shrink-0 w-[140px] sm:w-auto snap-start rounded-lg border border-primary/15 bg-background/30 p-2.5"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="font-display text-[9px] tracking-[0.25em] text-primary/80">
                          VẬN {dv.so_thu}
                        </span>
                        <span className="font-serif-soft text-[11px] italic text-gold-gradient">
                          {dv.chi}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] text-foreground/85">
                        {dv.tuoi_bat_dau}–{dv.tuoi_ket_thuc} tuổi
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        từ năm {dv.nam_bat_dau}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      {apiChart && !opened && (
        <div className="pointer-events-none fixed inset-x-0 bottom-24 z-50 px-4 md:hidden flex justify-center">
          <button
            type="button"
            data-tour="tuvi-khaimo-mobile"
            onClick={handleOpen}
            className="pointer-events-auto flex items-center justify-center gap-2 rounded-full bg-[#E5B869] px-6 py-2.5 text-sm font-semibold text-[#3e2504] shadow-[0_8px_20px_rgba(229,184,105,0.3)] transition active:scale-[0.98] border border-[#d6a54c]"
          >
            <Sparkles className="h-4 w-4" />
            Khai mở lá số ngay
          </button>
        </div>
      )}
      {/* Backdrop Overlay for Chat Drawer */}
      {(showOverviewChat || showCungChat) && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-500 animate-in fade-in"
          onClick={() => {
            setShowOverviewChat(false);
            setShowCungChat(false);
          }}
        />
      )}

      {/* Sliding Chat Drawer */}
      <div
        data-tuvi-chat-drawer="true"
        className={`app-drawer-viewport fixed top-0 right-0 z-[80] flex flex-col bg-[#fdfcf9]/95 dark:bg-[#121211]/95 border-l border-[#b8860b]/20 dark:border-[#E5B869]/20 shadow-[0_0_30px_rgba(0,0,0,0.08)] dark:shadow-[0_0_30px_rgba(0,0,0,0.8)] transition-all duration-500 ease-in-out backdrop-blur-md ${
          showOverviewChat || showCungChat
            ? "translate-x-0 w-full md:w-[480px]"
            : "translate-x-full w-full md:w-[480px]"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#b8860b]/15 dark:border-[#E5B869]/10 bg-[#f6ebd4]/90 dark:bg-[#1e1e1b]/90 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-serif-soft text-sm font-semibold text-[#E5B869] uppercase tracking-wider">
              {showOverviewChat ? "Đàm Đạo Đại Cục" : `Đàm Đạo Cung ${pinnedCung}`}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowOverviewChat(false);
              setShowCungChat(false);
            }}
            className="rounded-full p-1.5 text-foreground/50 dark:text-white/50 hover:text-[#E5B869] hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-300 active:scale-95"
            aria-label="Đóng chat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Chat Body */}
        <div className="flex-1 min-h-0 p-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] sm:p-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
          {showOverviewChat && (
            <MysticChat
              compact
              kind="copilot"
              variant="talisman"
              cardTitlePrefix="HỎI THÊM"
              subject="Tử Vi"
              resetKey={`followup-${apiChart?.id ?? seed}-${historySessionId ?? selectedDivinerId}`}
              placeholder="Hỏi thêm về lá số, vận hạn hoặc một cung cụ thể..."
              divinerId={selectedDivinerId}
              tuviChartId={apiChart?.id}
              initialSessionId={historySessionId}
              initialMessages={followUpHistoryMessages}
            />
          )}
          {showCungChat && pinnedCung && (
            <MysticChat
              compact
              kind="copilot"
              variant="talisman"
              cardTitlePrefix="HỎI THÊM"
              subject={pinnedCung}
              cungKey={pinnedCung ? TUVI_CUNG_KEY_BY_NAME[pinnedCung] : undefined}
              resetKey={`pin-${apiChart?.id ?? seed}-${pinnedCung}-${historySessionId ?? ""}`}
              placeholder={`Hỏi sâu hơn về cung ${pinnedCung}...`}
              divinerId={selectedDivinerId}
              tuviChartId={apiChart?.id}
              initialSessionId={historySessionId}
              initialMessages={followUpHistoryMessages}
            />
          )}
        </div>
      </div>

      <BoideSettingsDialog
        firstTime
        open={profileDialogOpen}
        onOpenChange={handleProfileDialogChange}
      />
      {apiChart && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceType="tuvi"
          resourceId={apiChart.id}
          captureRef={chartInnerRef}
          onMaskChange={setShareMaskActive}
        />
      )}
      <TuviChartsDrawer
        open={chartsDrawerOpen}
        onOpenChange={setChartsDrawerOpen}
        onSelect={(id) => void loadChartById(id)}
        currentId={apiChart?.id}
      />
      <TrustProbeDialog
        probeId={trustProbeId}
        open={trustProbeOpen}
        onOpenChange={setTrustProbeOpen}
      />
      {confirmDialog}
    </>
  );
}

function PreReadingSummary({
  chart,
  name,
  onOpen,
}: {
  chart: ChartView;
  name: string;
  onOpen: () => void;
}) {
  const menhCung = chart.cungs.find((cung) => cung.cung === "Mệnh")?.chi ?? "—";
  const thanCung = chart.cungs.find((cung) => cung.isThan)?.cung ?? "—";
  const menhCuc = `${chart.cuc}${chart.apiMeta?.soCuc ? ` (${chart.apiMeta.soCuc})` : ""}`;

  return (
    <div className="flex h-full flex-col px-4 py-5 text-left lg:px-5 lg:py-6">
      <div className="flex items-center justify-between gap-3">
        <span className="font-display text-[9px] tracking-[0.35em] text-primary">
          TỔNG QUAN LÁ SỐ
        </span>
        <span className="rounded-full border border-primary/25 bg-primary/5 px-2.5 py-1 text-[9px] font-medium tracking-wider text-primary">
          CHƯA KHAI MỞ
        </span>
      </div>

      <div className="mt-5 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-gold-soft bg-background/45 text-primary shadow-sm">
          <ScrollText className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="truncate font-serif-soft text-2xl italic text-gold-gradient">
            Lá số của {name || "bạn"}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Bàn lá số đã an xong. Bạn có thể xem cấu trúc mệnh cục miễn phí trước khi mở phần luận
            giải chuyên sâu.
          </p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-2.5">
        {[
          ["Bản mệnh", chart.nguHanh],
          ["Mệnh cục", menhCuc],
          ["Mệnh an tại", menhCung],
          ["Thân cư", thanCung],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-gold-soft/70 bg-background/35 px-3 py-3"
          >
            <dt className="font-display text-[8px] tracking-[0.22em] text-primary/75">
              {label.toUpperCase()}
            </dt>
            <dd className="mt-1 font-serif-soft text-sm italic text-foreground">{value}</dd>
          </div>
        ))}
      </dl>

      {chart.apiMeta?.menhCucRelation && (
        <div className="mt-3 rounded-2xl border border-primary/20 bg-primary/5 px-3.5 py-3">
          <p className="font-display text-[8px] tracking-[0.25em] text-primary/80">
            QUAN HỆ MỆNH — CỤC
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">
            {chart.apiMeta.menhCucRelation}
          </p>
        </div>
      )}

      <div className="mt-5 border-t border-primary/15 pt-4">
        <p className="font-display text-[9px] tracking-[0.3em] text-primary/80">SAU KHI KHAI MỞ</p>
        <ol className="mt-3 flex flex-col gap-2.5 text-xs leading-relaxed text-muted-foreground">
          <li className="flex gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
              1
            </span>
            Nhận luận giải Đại cục để nắm bức tranh tổng thể.
          </li>
          <li className="flex gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
              2
            </span>
            Chọn từng cung trên bàn số để xem sâu và đàm đạo tiếp.
          </li>
        </ol>
      </div>

      <button
        type="button"
        data-tour="tuvi-khaimo-panel"
        onClick={onOpen}
        className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-5 py-3 text-sm font-semibold text-primary-foreground shadow-gold transition hover:scale-[1.01] active:scale-[0.99]"
      >
        <Sparkles className="h-4 w-4" />
        Khai mở luận giải
      </button>
      <p className="mt-2 text-center text-[10px] leading-relaxed text-muted-foreground">
        Bạn sẽ được xác nhận trước nếu thao tác có tính phí.
      </p>
    </div>
  );
}

function HoverCard({ cung, data, score }: { cung: string; data: CungData; score?: number }) {
  const summaries: Record<string, string> = {
    Mệnh: "Bản chất, khí chất, cốt cách — soi rọi toàn bộ vận trình một đời.",
    "Phụ Mẫu": "Cha mẹ, huyết thống, phúc trạch tổ tiên để lại.",
    "Phúc Đức": "Tâm thức, hạnh phúc nội tại, phúc báo tích lũy.",
    "Điền Trạch": "Nhà cửa, đất đai, tài sản cố định, môi trường sống.",
    "Quan Lộc": "Sự nghiệp, công danh, chức vị và đường thăng tiến.",
    "Nô Bộc": "Bằng hữu, người dưới quyền, mối quan hệ xã hội.",
    "Thiên Di": "Di chuyển, xuất ngoại, vận động ngoài quê hương.",
    "Tật Ách": "Sức khỏe, bệnh tật, trắc trở thân thể.",
    "Tài Bạch": "Tiền bạc, tài lộc, khả năng tích lũy của cải.",
    "Tử Tức": "Con cái, hậu duệ, mối quan hệ thế hệ sau.",
    "Phu Thê": "Hôn nhân, bạn đời, duyên phận lứa đôi.",
    "Huynh Đệ": "Anh chị em, đồng môn, sự tương trợ ngang hàng.",
  };
  return (
    <div className="relative h-full rounded-2xl border border-primary/60 bg-card/95 backdrop-blur-md p-6 shadow-gold flex flex-col overflow-y-auto">
      <span className="font-display text-[10px] tracking-[0.4em] text-primary">
        LUẬN CUNG
        {data.isThan && (
          <span className="ml-2 rounded-sm bg-primary/30 px-1.5 py-0.5 text-[8px] tracking-normal text-primary-foreground">
            THÂN AN TẠI ĐÂY
          </span>
        )}
      </span>
      <div className="mt-1 flex items-center gap-3">
        <h2 className="font-serif-soft text-3xl italic text-gold-gradient">{cung}</h2>
        {typeof score === "number" && (
          <CungScoreBadge score={score} bandKey={bandKeyForScore(score)} />
        )}
      </div>
      <p className="mt-3 text-sm text-foreground/90">{summaries[cung]}</p>

      <div className="mt-4 rounded-xl border border-gold-soft bg-background/40 p-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] text-primary font-display">
            AN TẠI {data.thienCan ? `${data.thienCan.toUpperCase()} ` : ""}
            {data.chi.toUpperCase()}
          </div>
          <div className="flex gap-2 text-[9px] tracking-[0.2em]">
            {data.daiVanTuoi && (
              <span className="rounded bg-primary/15 px-1.5 py-0.5 text-primary">
                ĐV {data.daiVanTuoi}
              </span>
            )}
            {data.tieuVanNam && (
              <span className="rounded bg-element-water/20 px-1.5 py-0.5 text-element-water">
                TV {data.tieuVanNam}
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 space-y-1.5 text-sm">
          {data.chinh.length > 0 ? (
            data.chinh.map((s) => (
              <div key={s.name} className="flex items-baseline justify-between gap-2">
                <span className={`font-serif-soft text-base ${elementClass(s.element)}`}>
                  {s.name}
                  {s.trangThai && (
                    <span className="ml-1.5 font-sans text-[10px] not-italic text-foreground/65">
                      ({s.trangThai})
                    </span>
                  )}
                </span>
                <span className="text-[10px] text-muted-foreground shrink-0">Chính tinh</span>
              </div>
            ))
          ) : (
            <div className="italic text-muted-foreground">Vô chính diệu</div>
          )}
          {data.phu.map((s) => (
            <div key={s.name} className="flex items-baseline justify-between gap-2">
              <span className={`text-sm ${elementClass(s.element)}`}>
                {s.name}
                {s.trangThai && (
                  <span className="ml-1.5 text-[10px] text-foreground/60">({s.trangThai})</span>
                )}
              </span>
              <span className="text-[10px] text-muted-foreground shrink-0">Phụ tinh</span>
            </div>
          ))}
        </div>
        {(data.cat.length > 0 || data.hung.length > 0) && (
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-primary/20 pt-3 text-[11px]">
            <div>
              <div className="mb-1 font-display text-[9px] tracking-[0.25em] text-element-wood">
                CÁT
              </div>
              <div className="text-element-wood/90">
                {data.tieuAll
                  .filter((t) => data.cat.includes(t.name))
                  .map((t) => (t.trangThai ? `${t.name} (${t.trangThai})` : t.name))
                  .join(" · ") || "—"}
              </div>
            </div>
            <div>
              <div className="mb-1 font-display text-[9px] tracking-[0.25em] text-element-fire">
                HUNG
              </div>
              <div className="text-element-fire/90">
                {data.tieuAll
                  .filter((t) => data.hung.includes(t.name))
                  .map((t) => (t.trangThai ? `${t.name} (${t.trangThai})` : t.name))
                  .join(" · ") || "—"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto pt-4 text-center">
        <span className="inline-flex items-center gap-1.5 text-[11px] italic tracking-wider text-primary animate-glow">
          ✦ Click vào Cung để đàm đạo chi tiết cùng Cổ Nhân ✦
        </span>
      </div>
    </div>
  );
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  /** Hiện dấu * đỏ — cùng quy ước với các ô bắt buộc khác trong app. */
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex w-full sm:w-auto flex-1 flex-col gap-1.5">
      <span className="font-display text-[9px] tracking-[0.3em] text-primary">
        {label.toUpperCase()}
        {required ? <span className="text-red-400"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function BigStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-0">
      <span className="font-display text-[8.5px] font-semibold tracking-[0.3em] text-primary/90">
        {label}
      </span>
      <span className="font-serif-soft text-[15px] italic leading-tight text-foreground whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-display text-[8px] tracking-[0.2em] text-primary/80">{label}</span>
      <span className="font-serif-soft text-[12px] text-foreground">{value}</span>
    </div>
  );
}

function ReadingFor({ cung, chart }: { cung: string; chart: ReturnType<typeof generateChart> }) {
  const data = chart.cungs.find((c) => c.cung === cung)!;
  const summaries: Record<string, string> = {
    Mệnh: "Cung Mệnh chủ về tính cách, khí chất và bản chất con người. Đây là cung quan trọng bậc nhất, soi rọi toàn bộ vận trình.",
    "Phụ Mẫu": "Cung Phụ Mẫu nói về cha mẹ, quan hệ huyết thống và phúc trạch tổ tiên để lại.",
    "Phúc Đức": "Cung Phúc Đức luận về tâm thức, hạnh phúc nội tại và phúc báo tích lũy.",
    "Điền Trạch": "Cung Điền Trạch chủ về nhà cửa, đất đai, tài sản cố định và môi trường sống.",
    "Quan Lộc": "Cung Quan Lộc luận sự nghiệp, công danh, chức vị và đường công danh.",
    "Nô Bộc": "Cung Nô Bộc nói về bằng hữu, người dưới quyền và mối quan hệ xã hội.",
    "Thiên Di": "Cung Thiên Di chủ về di chuyển, xuất ngoại, vận động ngoài quê hương.",
    "Tật Ách": "Cung Tật Ách luận về sức khỏe, bệnh tật và những trắc trở thân thể.",
    "Tài Bạch": "Cung Tài Bạch chủ về tiền bạc, tài lộc và khả năng tích lũy của cải.",
    "Tử Tức": "Cung Tử Tức luận về con cái, hậu duệ và mối quan hệ với thế hệ sau.",
    "Phu Thê": "Cung Phu Thê nói về hôn nhân, bạn đời và duyên phận lứa đôi.",
    "Huynh Đệ": "Cung Huynh Đệ chủ về anh chị em, đồng môn và sự tương trợ ngang hàng.",
  };
  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-foreground/85">{summaries[cung]}</p>
      <div className="rounded-xl border border-gold-soft bg-background/40 p-4">
        <div className="flex items-center justify-between">
          <span className="font-display text-[10px] tracking-[0.3em] text-primary">
            AN TẠI {data.chi.toUpperCase()}
          </span>
        </div>
        <div className="mt-3 space-y-2 text-sm">
          {data.chinh.length > 0 ? (
            data.chinh.map((s) => (
              <div key={s.name} className="flex items-baseline justify-between">
                <span className={`font-serif-soft text-base ${elementClass(s.element)}`}>
                  {s.name}
                </span>
                <span className="text-[10px] text-muted-foreground">Chính tinh</span>
              </div>
            ))
          ) : (
            <div className="text-sm italic text-muted-foreground">Vô chính diệu</div>
          )}
          {data.phu.map((s) => (
            <div key={s.name} className="flex items-baseline justify-between">
              <span className={`text-sm ${elementClass(s.element)}`}>{s.name}</span>
              <span className="text-[10px] text-muted-foreground">Phụ tinh</span>
            </div>
          ))}
        </div>
        {(data.cat.length > 0 || data.hung.length > 0) && (
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-primary/20 pt-3 text-[11px]">
            <div>
              <span className="font-display text-[9px] tracking-[0.25em] text-element-wood">
                CÁT •{" "}
              </span>
              <span className="text-element-wood/90">{data.cat.join(" · ") || "—"}</span>
            </div>
            <div>
              <span className="font-display text-[9px] tracking-[0.25em] text-element-fire">
                HUNG •{" "}
              </span>
              <span className="text-element-fire/90">{data.hung.join(" · ") || "—"}</span>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs italic leading-relaxed text-muted-foreground">
        Sao chính diệu định cốt cách, sao phụ tinh trợ thành bại, tiểu tinh điểm xuyết phong vận.
        Hãy chiêm nghiệm chỉnh thể, không chấp một sao.
      </p>
    </div>
  );
}
